/* 新規ゲーム追加ツールの i18n（日本語⇔英語）。
 *
 * 方式: ソースは日本語を正とし、描画後の DOM テキスト（テキストノード・title/placeholder 属性）と
 * alert()/confirm() の文言を辞書で英訳する。コード側の全文字列書き換えを不要にし、
 * 文言追加時は本ファイルの辞書へ ja/en をセットで追加する（CLAUDE.md ルール 2 の適用対象）。
 * 変換は ①完全一致 → ②パターン（埋め込み値つき文言）→ ③ラベル用トークン置換 の三段。
 * 未登録文言は日本語のまま表示される（機能は壊れない）。
 * このファイルは Node 版では <script> で、ブラウザ版では tool-entry.mjs から import で読み込まれる。
 */
(() => {
    'use strict';

    const STORE_KEY = 'vn-lang';
    const hasDom = typeof document !== 'undefined';
    // 言語の決定順: ① URL パス（/en/... または /ja/... の明示指定が最優先・以後の既定として記憶）
    //             ② 前回の選択（localStorage） ③ ブラウザ言語（日本語なら ja・それ以外は en）
    const pathLang = (typeof location !== 'undefined' && /^\/(en|ja)(\/|$)/.exec(location.pathname || '')?.[1]) || null;
    const saved = (typeof localStorage !== 'undefined') ? localStorage.getItem(STORE_KEY) : null;
    const browserLang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'ja';
    const lang = pathLang ?? (saved === 'en' || saved === 'ja' ? saved : (browserLang.toLowerCase().startsWith('ja') ? 'ja' : 'en'));
    if (pathLang && saved !== pathLang && typeof localStorage !== 'undefined') localStorage.setItem(STORE_KEY, pathLang);

    const CJK = /[ぁ-んァ-ヶ一-龯]/;

    // ── ① 完全一致辞書（ja → en）────────────────────────
    const EXACT = {
        // ヘッダ・共通
        'VoxelNovels 3Dゲーム自動生成ツール': 'VoxelNovels 3D Game Generator',
        '3Dゲーム自動生成ツール': '3D Game Generator',
        'GLB プレビュー - 3Dゲーム自動生成ツール': 'GLB Preview - 3D Game Generator',
        'ゲーム制作メニュー': 'Game Creation',
        '物語から、遊べる世界を組み立てる。': 'Build a playable world from your story.',
        'メインメニュー': 'Main menu',
        'プロジェクト一覧': 'Projects',
        'プロンプト作成': 'Prompt builder',
        '生成サービス設定': 'Generation services',
        'アカウント・表示': 'Account & display',
        '表示言語': 'Display language',
        'リリースノート': 'Release Notes',
        '運営者情報・免責事項': 'Legal & Disclaimer',
        'ご意見・ご要望': 'Feedback',
        'サイドバーを開閉': 'Toggle sidebar',
        'サイドバーを閉じる': 'Close sidebar',
        'VoxelNovels公式サイト': 'VoxelNovels official site',
        'ツールメニュー': 'Tool menu',
        'ツールサイドバー': 'Tool sidebar',
        '制作メニュー': 'Creation menu',
        'ホーム': 'Home',
        'ダッシュボード': 'Dashboard',
        '新規ゲーム作成（スキャフォールド）': 'New game (scaffold)',
        'ブラウザの保存容量が不足しています。ホーム画面から不要な物語プロジェクトを削除してから再実行してください。': 'Browser storage is full. Delete unneeded story projects from the Home screen and try again.',
        'AI 依頼文': 'AI Request',
        '外部AI依頼': 'External AI Request',
        '依頼文書作成': 'Request document',
        'ChatGPTやGeminiなどの外部AIへ渡す依頼文書を作成します': 'Create a request document to use with an external AI such as ChatGPT or Gemini',
        'API設定': 'API Settings',
        'ログイン': 'Sign in',
        '読み込み中……': 'Loading…',
        '処理中…': 'Processing…',
        '処理中です。完了までお待ちください…': 'Processing. Please wait until it finishes…',
        '処理を開始しています…': 'Starting…',
        '一括実行を開始しています…': 'Starting batch run…',
        '（実行待ち）': '(waiting to run)',
        '不明なエラー': 'Unknown error',
        '待機': 'Queued',
        '実行中…': 'Running…',
        '完了': 'Done',
        '準備済み': 'Ready',
        '失敗': 'Failed',
        '中断': 'Canceled',
        'ホームのゲームへ戻りました': 'Returned to your Home game',
        'ホームのゲームを開始する': 'Start the Home game',
        'ホームのゲームへ戻る': 'Return to the Home game',
        'ゲーム連邦へ戻りました': 'Returned to the Game Federation',
        '侵入中のゲームを終了しています…': 'Ending the visited game…',
        '侵入中のゲームを終了し、ゲーム連邦へ戻りました': 'Ended the visited game and returned to the Game Federation',
        '持ち込めない項目': 'Items unavailable at the destination',
        '理由不明': 'Unknown reason',
        '侵入先を準備しています。しばらくお待ちください…': 'Preparing the destination. Please wait…',
        '侵入前の確認を取得しています。しばらくお待ちください…': 'Loading the visit preview. Please wait…',
        'ホームのゲームキャラクターで、別のゲームへ侵入できます。': 'Use a character from your Home game to visit another game.',
        'ホームへ戻っています。しばらくお待ちください…': 'Returning to your Home game. Please wait…',
        'ホームのゲームを再開できませんでした': 'Could not resume your Home game',
        'ゲーム設定を保存しています…': 'Saving game settings…',
        '保存': 'Save',
        '削除': 'Delete',
        'コピー': 'Copy',
        '実行': 'Run',
        '再読み込み': 'Reload',
        '画面を更新': 'Refresh view',
        'はい': 'Yes',
        'あり': 'Yes',
        'なし': 'No',
        '既定': 'Default',
        '（既定）': '(default)',
        '未配置': 'Missing',
        '参照': 'referenced',
        // ダッシュボード
        'はじめに: API キーの設定が必要です': 'First step: set up your API key',
        '物語の解析・ゲーム生成には OpenRouter の API キー（ご自身で発行）が必要です。': 'Analyzing stories and generating games requires your own OpenRouter API key.',
        '「API設定」でキーを保存し、「接続テスト」まで実行すると、すべての生成機能が使えるようになります。': 'Save your key in "API Settings" and run "Connection test" to unlock all generation features.',
        'API設定を開く': 'Open API Settings',
        'AI接続状態': 'AI connection',
        '物語の文書': 'Story document',
        'テキストファイルを選ぶか、物語の本文を貼り付けてください。': 'Select a text file or paste the story text.',
        '📖 物語から新規作成': '📖 Create new from a story',
        '＋ 新規ゲーム作成（手動）': '+ New game (manual)',
        '作成中のゲーム': 'Games in progress',
        '物語プロジェクト（F13〜F16）': 'Story projects (F13–F16)',
        '物語プロジェクトはまだありません。「物語から新規作成」で小説テキストを登録すると、解析→ゲーム生成→キャラ生成→ワールド設計→JSON 生成まで進められます。': 'No story projects yet. Use "Create new from a story" to register novel text, then proceed through analysis → game generation → character generation → world design → JSON generation.',
        'newgames/ 配下にゲームはまだありません。上の「物語から新規作成」または「新規ゲーム作成（手動）」から始めてください。': 'No games yet. Start with "Create new from a story" or "New game (manual)" above.',
        '（未解析）': '(not analyzed)',
        '（未解析の物語）': '(unanalyzed story)',
        '未生成': 'not created',
        '▶ プレビュー': '▶ Preview',
        '▶ プレビューを開く': '▶ Open preview',
        '公開済み': 'Published',
        'スキーマ': 'Schema',
        '整合': 'Integrity',
        '翻訳': 'i18n',
        'アセット': 'Assets',
        '未実行': 'not run',
        '✔ 取り込みまで完了': '✔ Completed through import',
        'ゲーム一覧': 'Games',
        // 物語から作成
        '物語から新規ゲーム作成': 'Create a new game from a story',
        '接続:': 'Connection:',
        '✔ 利用可能': '✔ Available',
        '✖ 接続不可': '✖ Unreachable',
        '未設定': 'not set',
        '（接続テスト未実施）': '(connection not tested)',
        // 📖物語から新規ゲーム作成の2ステップ化（2026-07-26 指示: 小説ファイル → ゲームシナリオ文書）
        '小説ファイルを選択（.txt / .md）': 'Choose a novel file (.txt / .md)',
        '小説ファイルを読み込み、「ゲームシナリオ文書作成」でゲーム生成用の文書にまとめます。すでにゲームシナリオ文書がある場合は、下の欄へ直接貼り付けても進められます。': 'Load a novel file, then use \u201cCreate game scenario document\u201d to condense it into a document for game generation. If you already have one, you can paste it into the field below instead.',
        'ファイルは選択されていません': 'No file selected',
        'ゲームシナリオ文書作成': 'Create game scenario document',
        'ゲームシナリオ文書の作成': 'Game scenario document creation',
        'または、ゲームシナリオ文書（１万文字以内）を張り付け': 'Or paste a game scenario document (up to 10,000 characters)',
        '読み込んだファイル:': 'Loaded file:',
        '先に小説ファイルを選択してください。': 'Select a novel file first.',
        '解析コストを見積もっています…': 'Estimating the analysis cost…',
        '予測値です。実際の請求額は OpenRouter の実費が正です。': 'This is an estimate; the actual charge from OpenRouter is authoritative.',
        '文書が長いほど時間がかかります。': 'Longer documents take more time.',
        '解析コストを見積もれませんでした:': 'Could not estimate the analysis cost:',
        '下の「ゲームシナリオ文書」欄の内容を、生成した文書で置き換えますか？': 'Replace the contents of the game scenario document field below with the newly generated document?',
        '小説全文を LLM で読み込むため、OpenRouter に実費が発生します（文書が長いほど時間がかかります）。': 'The whole novel is sent to the LLM, so OpenRouter charges apply (longer documents take more time).',
        'ゲームシナリオ文書の作成に失敗しました': 'Failed to create the game scenario document',
        '先に「ゲームシナリオ文書作成」を実行してください。': 'Run “Create game scenario document” first.',
        '読み込んだ小説はそのままでは解析に使いません（長すぎるため、ゲームシナリオ文書にまとめてから解析します）。': 'The loaded novel is not analyzed as-is: it is too long, so it is condensed into a game scenario document first.',
        'テキストファイルを選択（.txt / .md）': 'Choose a text file (.txt / .md)',
        'または本文を貼り付け': 'Or paste the text',
        '（200字以上必要です）': '(200+ characters required)',
        '表示用タイトル（ファイル名。省略可）': 'Display title (file name, optional)',
        '例: 時計仕掛けの都.txt': 'e.g. clockwork-city.txt',
        '作成後、残りの工程を一括実行する（① 解析〜⑥ 取り込み。開始前に工程一覧と実費の目安を確認できます）': 'After creation, batch-run the remaining steps (① Analysis – ⑥ Import; you can review the steps and estimated cost before starting)',
        'プロジェクトを作成して解析へ': 'Create project and analyze',
        '作成に失敗しました': 'Failed to create',
        // 一括実行・進捗
        '🚀 未作成を一括実行': '🚀 Batch-run missing steps',
        '次の作業:': 'Next step:',
        '次の作業: すべて完了': 'Next step: All steps complete',
        'すべて完了': 'All steps complete',
        '全工程が完了しています。': 'All steps are complete.',
        '未作成の工程はありません。前工程を変更した場合に再び実行できます。': 'There are no missing steps. This becomes available again after an earlier step is changed.',
        '未完了の工程を順番に実行します': 'Runs the incomplete steps in order',
        '中断しました。「🚀 未作成を一括実行」でいつでも続きから再開できます。': 'Canceled. You can resume anytime with "🚀 Batch-run missing steps".',
        '該当タブで個別に再実行するか、原因を解消してから「🚀 未作成を一括実行」で失敗した工程から再開できます。': 'Re-run the step in its tab, or fix the cause and resume from the failed step with "🚀 Batch-run missing steps".',
        '✔ 全工程が完了しました。プレビューでゲームの動作を確認してください。': '✔ All steps completed. Open the preview to check the game.',
        '① 解析': '① Analysis',
        '② ゲーム生成': '② Game generation',
        '② ベースデータ生成': '② Base data generation',
        '② ベースデータを生成': '② Generate base data',
        'ゲーム作成に必要な準備データを作成します。': 'Creates the preparation data needed to build the game.',
        '③ キャラ生成': '③ Character generation',
        '物語に登場するキャラクターの画像と3Dモデルを作成します。': 'Creates images and 3D models for the story characters.',
        '③-1 画像生成プロンプト': '③-1 Image-generation prompts',
        '③-2 画像の自動生成': '③-2 Auto-generate images',
        '③-3 3Dモデルの自動生成': '③-3 Auto-generate 3D models',
        '3Dモデル実費の目安: 1体 ≒30〜39P × 未生成体数（Meshy クレジット / 1体あたり数分〜30分）': 'Estimated 3D model cost: ≈30–39 credits per cast × casts without models (Meshy credits / minutes to ~30 min per cast)',
        '3Dモデル未生成のキャストがありますが、Meshy API キーが未設定（または接続テスト未実施）です。': 'Some casts have no 3D models yet, but the Meshy API key is not set (or the connection test has not been run).',
        'OK ＝ 3Dモデル生成をスキップして残りの工程を実行': 'OK = skip 3D model generation and run the remaining steps',
        'キャンセル ＝ 中止（「API設定」でキーを設定してから再実行）': 'Cancel = stop (set the key in "API Settings", then run again)',
        '未完了の工程はありません（残りは 3Dモデル生成のみです。③ キャラ生成の画面で個別生成してください）。': 'No steps remain (only 3D model generation is left; generate models individually on the ③ Character generation screen).',
        '④ ワールド設計': '④ World design',
        '⑥-2 JSON 生成': '⑥-2 JSON generation',
        '⑥-3 取り込みと検証': '⑥-3 Import & validation',
        '⑥ JSON設計': '⑥ JSON design',
        '3D モデル': '3D models',
        // プロジェクト画面
        'プロジェクトが見つかりません': 'Project not found',
        '（原文なし: Git 管理外のため再登録が必要）': '(source text missing: re-register it — it is not tracked)',
        'ゲーム:': 'Game:',
        '物語:': 'Story:',
        '物語': 'Story',
        '進捗': 'Progress',
        'AI実行': 'AI runs',
        // ホームの作品カード（項目｜内容の表）
        'プロジェクトID': 'Project ID',
        '計算中…': 'Calculating…',
        'これまでに実行した生成処理のログを表示します。': 'Shows logs from generation tasks run so far.',
        // ① 解析タブ
        '① 物語解析': '① Story analysis',
        '物語から作品設定・キャラクター・舞台案を作成します。結果はあとから編集できます。': 'Creates the story settings, characters, and setting plan. You can edit the results later.',
        '再解析を実行': 'Re-run analysis',
        '解析結果を編集': 'Edit analysis results',
        '解析を実行': 'Run analysis',
        'ファイルの読み込み': 'Load story file',
        '物語の原文を再登録': 'Re-register story file',
        '解析元の物語文書を別のテキストファイルへ差し替え、解析以降の工程を初期化します': 'Replace the source story document with another text file and reset the analysis and all later steps',
        '物語の本文が短すぎます（200文字以上のテキストファイルが必要です）。': 'The story is too short (a text file with at least 200 characters is required).',
        '物語文書の読み込みに失敗しました': 'Failed to load the story document',
        '未解析です。': 'Not analyzed yet.',
        '解析結果を保存': 'Save analysis',
        'ゲーム設定案': 'Proposed game settings',
        'ゲーム設定案を編集': 'Edit game settings',
        'ゲーム設定案を上書き保存しますか？': 'Overwrite the proposed game settings?',
        '編集した内容は作成済みの工程データ（ゲーム開始画面のタイトル・原作リンク等）へ直接反映されます。他の工程の作り直しは不要です。': 'Your edits are applied directly to the already-created pipeline data (such as the start-screen title and the original-work link). No other steps need to be redone.',
        'サブタイトル': 'Subtitle',
        'サブタイトル（40文字以内）': 'Subtitle (up to 40 characters)',
        'サブタイトルは40文字以内にしてください。': 'Keep the subtitle within 40 characters.',
        '原作URL': 'Original work URL',
        '原作URLは、読み込んだ小説文書内に記載された「なろう・カクヨム・Royal Road」等の対応小説公開サイトURLだけを使用します。文書にURLがなければフッターの原作リンクは表示されません。': 'The original-work URL must be a supported fiction-publishing URL (such as Shosetsuka ni Naro, Kakuyomu, or Royal Road) written in the loaded story document. If the document has no URL, the footer link is hidden.',
        '原作URLは、読み込んだ小説文書内に記載された対応小説公開サイトのURLだけを設定できます。URLは保存されませんでした。': 'The original-work URL must be a supported fiction-publishing URL written in the loaded story document. The URL was not saved.',
        'タイトル（日本語）': 'Title (Japanese)',
        'タイトル（英語）': 'Title (English)',
        '世界設定': 'World setting',
        '紹介文（日本語）': 'Description (Japanese)',
        '紹介文（英語）': 'Description (English)',
        '変更を保存': 'Save changes',
        '保存しています…': 'Saving…',
        '（なし）': '(none)',
        '物語敵タイプ登録（ランク能力倍率・生成GLBを反映）': 'Register story enemy types (rank stat multipliers and generated GLBs)',
        '開始ワールド': 'Starting world',
        'タイトル': 'Title',
        'ジャンル': 'Genre',
        '主人公': 'Protagonist',
        '作品要約シート（12項目・自動推測）': 'Story summary sheet (12 items, auto-inferred)',
        'ID区分名前人型 / ポーズ外見（画像プロンプトの元）': 'IDRoleNameHumanoid / PoseAppearance (image prompt source)',
        '保存に失敗': 'Save failed',
        // ③ プロンプトタブ
        '③ キャラ生成（画像生成プロンプト → キャラ画像 → 3DモデルGLB）': '③ Character generation (image-generation prompt → character image → 3D model GLB)',
        'プレイアブルはリギング向けの A 字型、人型 NPC・敵はシーンに合う自然な姿、非人型は自然体の英語画像プロンプトを OpenRouter で生成します。': 'Generates English image prompts via OpenRouter: A-pose for playable characters (rig-ready), natural in-scene poses for humanoid NPCs/enemies, and natural poses for non-humanoids.',
        'プロンプトを生成': 'Generate prompts',
        '画像生成モデル': 'Image model',
        '全画像の再生成': 'Regenerate all images',
        '全GLBの再生成': 'Regenerate all GLBs',
        '一括画像生成（一部）': 'Bulk image generation (partial)',
        '全GLBの再生成（一部）': 'Regenerate all GLBs (partial)',
        'GLB を生成できるキャストがありません（先に画像を生成してください）。': 'No casts can generate GLBs (generate images first).',
        '（画像を変更していないキャストは Meshy タスクキャッシュを再利用＝追加消費 0P で再配置されます）': '(Casts whose images are unchanged reuse the Meshy task cache and are re-placed at no extra cost)',
        '画像を自動生成': 'Auto-generate image',
        '画像変更': 'Replace image',
        'プロンプト表示': 'Show prompt',
        '英語プロンプトをコピー': 'Copy English prompt',
        'コピーしました': 'Copied',
        '再生成': 'Regenerate',
        'A字型': 'A-pose',
        '自然体（シーン）': 'Natural (in scene)',
        '自然体': 'Natural',
        'ファイル:': 'File:',
        '推奨 GLB:': 'Recommended GLB:',
        '画像情報': 'Image info',
        '画像ファイル': 'Image file',
        'GLB(待機)': 'GLB (idle)',
        'GLB(移動)': 'GLB (move)',
        'GLB(戦闘)': 'GLB (battle)',
        'GLB(モデル)': 'GLB (model)',
        'GLB(統合)': 'GLB (unified)',
        '未生成（「画像を自動生成」または「画像変更」で外部画像を登録）': 'none yet (use "Auto-generate image" or "Replace image" to register one)',
        '未作成': 'not created yet',
        '生成ログ表示': 'Show generation logs',
        '生成ログ': 'Generation logs',
        '画像生成ログ': 'Image generation log',
        '画像プロンプト生成ログ': 'Image-prompt generation log',
        'GLB生成ログ': 'GLB generation log',
        '解析コスト': 'Analysis cost',
        'プロンプト生成コスト': 'Prompt generation cost',
        '画像生成コスト': 'Image generation cost',
        'GLB生成コスト': 'GLB generation cost',
        '設計コスト': 'Design cost',
        '生成コスト': 'Generation cost',
        '生成ログ履歴': 'Generation log history',
        '一括実行の全工程ログと、個別実行したOpenRouter・Meshy等の有料APIログを表示します。': 'Shows complete batch-run logs and paid API logs from individual OpenRouter, Meshy, and similar runs.',
        '（まだログがありません）': '(no logs yet)',
        'このキャストの画像生成・GLB生成の保存済みログ履歴を表示します': "Shows this cast's saved image/GLB generation log history",
        '一括実行全体と個別実行した有料APIの保存済みログ履歴を表示します': 'Shows saved complete batch-run logs and paid API logs from individual runs',
        '自動生成': 'auto-generated',
        'アップロード': 'uploaded',
        '・規制回避: grok へ切替': '・Policy fallback: switch to grok',
        '・規制回避: プロンプト自動修正': '・Policy fallback: auto-sanitize prompt',
        '該当キャストは grok へ切替': 'switch affected casts to grok',
        'プロンプト自動修正': 'auto-sanitize prompts',
        'そのまま生成（拒否リスクあり）': 'generate as-is (may be refused)',
        'OpenRouter（$ 直接課金・無料枠消費ゼロ。実費はログに表示）': 'OpenRouter (direct billing in $, no free-tier usage; actual cost shown in the log)',
        'GLB が見つかりません': 'GLB not found',
        '不正なファイル名です': 'Invalid file name',
        // ④ ワールド設計 / ⑤ 開始画面設計タブ
        'ワールド設計を実行': 'Run world design',
        '再設計を実行（上書き）': 'Re-run design (overwrite)',
        '設計結果': 'Design result',
        '設計内容を編集': 'Edit design',
        '未設計です。「ワールド設計を実行」を押してください。': 'Not designed yet. Select “Run world design.”',
        '未設計です（先に ① 解析が必要）。': 'Not designed yet (run ① Analysis first).',
        '設計を保存': 'Save design',
        // ⑤ 開始画面設計タブ
        '⑤ 開始画面設計': '⑤ Start screen design',
        '⑤ 開始画面設計の生成': '⑤ Start screen design generation',
        '開始画面素材の生成': 'Start screen art generation',
        '開始画面素材コスト': 'Start screen art cost',
        '⑤ 開始画面設計（背景・立ち絵）': '⑤ Start screen design (background & portraits)',
        '⑤ 開始画面設計（背景・キャラ選択立ち絵）': '⑤ Start screen design (background & character selection portraits)',
        'ゲーム開始画面は「環境背景」「選択キャラの全身立ち絵」「タイトル・操作UI（HTML/i18n）」を別レイヤーで重ねます。背景は④ ワールド設計に合わせて文字・人物なしで生成し、立ち絵は③キャラ生成の外見に合わせて生成します。自作画像への差し替えもできます。素材がそろうまでゲームは従来のシンプルな開始画面のままです。': 'The start screen layers an environment background, a full-body portrait of the selected character, and the HTML/i18n title & controls. The background is generated (no text, no people) to match ④ World design, and portraits match the appearance from ③ Character generation. You can also replace any image with your own. Until all art is ready, the game keeps its previous simple start screen.',
        '未生成分を自動生成': 'Generate missing art',
        'すべて再生成': 'Regenerate all',
        '背景・立ち絵の未生成分だけを自動生成します（生成済み・アップロード済みは課金し直しません）': 'Generates only the missing background/portraits (already generated or uploaded art is not billed again)',
        '背景とすべての立ち絵を作り直します（既存素材は上書き）': 'Recreates the background and all portraits (existing art is overwritten)',
        '背景画像（物語のカバーアート的な環境背景）': 'Background image (cover-art-style environment)',
        '背景を生成': 'Generate background',
        '背景を再生成': 'Regenerate background',
        '自作画像に差し替え': 'Replace with your own image',
        '④ ワールド設計から背景プロンプトを合成して生成します': 'Composes the background prompt from ④ World design, then generates',
        '背景プロンプト（英語・編集すると次回の背景生成で使用）': 'Background prompt (English; edits are used for the next background generation)',
        '（未生成。生成時に④ ワールド設計から自動合成されます）': '(Not generated yet. Composed automatically from ④ World design at generation time)',
        '背景には作品タイトル・文字・人物・UI を入れません（開始画面のタイトルと操作は HTML で重ねるため、日本語版・英語版で同じ画像を共用できます）。': 'The background contains no title, text, people, or UI (the title and controls are layered as HTML, so Japanese and English share the same image).',
        '③ キャラ生成の外見記述（英語プロンプト）と同じ内容から、開始画面用の自然な立ち姿・透明背景で生成します。GLB 用の A 字ポーズ画像はそのまま使いません。': 'Portraits are generated from the same appearance description (English prompt) as ③ Character generation, in a natural standing pose with a transparent background. The A-pose images for GLBs are not reused as-is.',
        '立ち絵を生成': 'Generate portrait',
        '立ち絵を再生成': 'Regenerate portrait',
        '楕円マスクで背景へ馴染ませる（透過でない画像向け）': 'Blend into the background with an elliptical mask (for non-transparent images)',
        '背景に描かれた台座（キャラの足場）の中心へ、立ち絵の基準位置を合わせます（全キャラ共通・ゲームの立ち絵位置へ反映）': 'Aligns the portrait base position with the center of the stage drawn in the background (shared by all characters; applied to the in-game portrait position)',
        '足元の透過を補正': 'Fix foot transparency',
        '背景画像の調整': 'Background image adjustments',
        '立ち絵の調整': 'Portrait adjustments',
        '上のキャラ切り替えボタンで選択中のキャラに適用されます': 'Applies to the character selected with the buttons above',
        '背景スケール・縦クロップ位置・キャラ基準位置を既定値に戻します（保存するまでゲームには反映されません）': 'Resets the background scale, vertical crop position, and portrait base position to their defaults (not applied to the game until you save)',
        '背景画像全体の縮尺です（拡大すると外周がクロップされます。横方向のズレはキャラ基準位置で調整）': 'Overall scale of the background image (enlarging crops the outer edges; adjust horizontal offset with the portrait base position)',
        '選択中キャラの位置・大きさ・マスクを既定値に戻します（保存するまでゲームには反映されません）': 'Resets the selected character\'s position, size, and mask to their defaults (not applied to the game until you save)',
        'ℹ 現在の背景は縦に余白が出ない横長のため、縦クロップ位置は「背景スケール」を上げたときに有効になります。': 'ℹ The current background is wide with no vertical margin, so the vertical crop position takes effect once you raise the background scale.',
        '背景スケール・クロップ位置・キャラ基準位置の調整は下の「配置プレビュー（近似）」で行えます。': 'Adjust the background scale, crop position, and portrait base position in the "Layout preview (approximate)" below.',
        '脚の間に残った白い空間と足元の床影を追加で透過します（画像を作り直さないため API 課金なし）': 'Additionally clears the white gap left between the legs and the floor shadow at the feet (no API cost; the image is not regenerated)',
        '画像は作り直さないため API 課金はありません。実行しますか？': 'The image is not regenerated, so there is no API cost. Run now?',
        '足元の透過を補正しています…': 'Fixing foot transparency…',
        '足元の透過補正に失敗しました': 'Failed to fix foot transparency',
        '補正対象が見つかりませんでした（すでに透過済みか、白残り・床影を検出できませんでした）。': 'Nothing to fix was found (already transparent, or no white gap / floor shadow was detected).',
        '⚠ 共有エンジン（/_runtime/）が開始画面アート未対応の旧ビルドです。エンジンを更新（ローカル開発は sevenfold で npm run build / 公開環境は共有ランタイムの再デプロイ）するまで、保存してもプレビューは従来の開始画面のままになります。': '⚠ The shared engine (/_runtime/) is an old build without start-screen art support. Until the engine is updated (local dev: npm run build in sevenfold / production: redeploy the shared runtime), the preview keeps the previous start screen even after saving.',
        '配置プレビュー（近似）': 'Layout preview (approximate)',
        '背景クロップと立ち絵の位置・大きさの目安です（16:9 画面基準。実ゲームは画面比率により背景の見える範囲が変わりますが、立ち絵と背景の相対的な大きさ・横位置は一致します）。数値を変えたら「調整を保存」でゲームへ反映されます。': 'A rough guide for the background crop and portrait position/size (based on a 16:9 screen; on other aspect ratios the visible background area differs, but the portrait\'s size and horizontal position relative to the background stay consistent). After changing values, use "Save adjustments" to apply them to the game.',
        '調整を保存（ゲームへ反映）': 'Save adjustments (apply to game)',
        'タイトル表示位置（HTML）': 'Title position (HTML)',
        '操作パネル位置': 'Control panel area',
        '未作成です（先に ① 解析が必要）。': 'Not created yet (run ① Analysis first).',
        '（④ ワールド設計の完了後に実行できます）': '(Available after completing ④ World design)',
        'ワールド設計（world-design.json）がありません。先に「④ ワールド設計」を実行してください。': 'No world design (world-design.json). Run "④ World design" first.',
        '④ ワールド設計（背景の生成に必要）': '④ World design (required for the background)',
        '③-1 画像生成プロンプト（立ち絵の生成に必要）': '③-1 Image-generation prompts (required for portraits)',
        '② ベースデータ生成（ゲームへの反映に必要）': '② Base data generation (required to apply to the game)',
        '開始画面の背景画像': 'the start screen background image',
        '相方': 'Partner',
        '作成済み': 'created',
        '（ゲームへ反映済み）': '(applied to the game)',
        '（素材がそろうまで従来画面）': '(previous screen until all art is ready)',
        '（現在の画像は上書きされ、ゲームへ即時反映されます）': '(The current image is overwritten and applied to the game immediately)',
        '1枚 ≒ $0.01〜0.05（選択中の画像モデルに依存）。実行しますか？': 'About $0.01–0.05 per image (depends on the selected image model). Run now?',
        '背景・立ち絵の未生成分を自動生成します。': 'Generates the missing background/portraits.',
        '背景とすべての立ち絵を作り直します（既存素材は上書きされます）。': 'Recreates the background and all portraits (existing art is overwritten).',
        '背景を再生成します（既存は上書き）。': 'Regenerates the background (the existing one is overwritten).',
        '背景を生成します。': 'Generates the background.',
        '開始画面素材（背景・立ち絵）は作成済みのためスキップしました。': 'Start screen art (background & portraits) already exists; skipped.',
        '生成の開始に失敗しました': 'Failed to start generation',
        '調整を保存しています…': 'Saving adjustments…',
        '✔ 保存してゲームへ反映しました': '✔ Saved and applied to the game',
        '✔ 保存しました（ゲーム生成後に反映されます）': '✔ Saved (applied after the game is generated)',
        '保存に失敗しました': 'Failed to save',
        // ⑥ JSON設計タブ
        '⑥-1 AI 依頼文': '⑥-1 AI request text',
        'すべて': 'All',
        '項目': 'Item',
        'ファイル': 'File',
        '操作': 'Action',
        '編集': 'Edit',
        'JSONを編集': 'Edit JSON',
        'キャンセル': 'Cancel',
        '保存後は、もう一度「取り込み実行」が必要です。': 'After saving, run Import again.',
        '生成した内容をゲームへ反映し、問題がないか確認します。': 'Applies the generated content to the game and checks it for problems.',
        '内容を確認しています…': 'Checking content…',
        '確認結果': 'Check result',
        '依頼文を生成': 'Generate request text',
        '依頼文を再生成': 'Regenerate request text',
        '編集内容を保存': 'Save edits',
        '生成しました（必要に応じて編集→保存）': 'Generated (edit and save as needed)',
        '保存しました（この内容で LLM 実行されます）': 'Saved (this text will be used for the LLM run)',
        '選択ユニットを生成': 'Generate selected units',
        'ユニットを選択してください。': 'Select at least one unit.',
        '取り込み実行': 'Run import',
        '未生成のJSONがあるため、先に⑥-2 JSON生成を完了してください': 'Complete ⑥-2 JSON generation first because some JSON units have not been generated.',
        'エラーがあるため取り込みを停止しました。ゲームデータは変更していません。JSON を再生成または修正してください。': 'Import stopped because errors were found. No game data was changed. Regenerate or fix the JSON.',
        '① 取り込みプラン（差分確認）': '① Import plan (diff preview)',
        '② 取り込み実行（検証つき）': '② Run import (with validation)',
        'エラーがあるため取り込みできません。JSON を再生成または修正して、もう一度プランを確認してください。': 'Cannot import due to errors. Regenerate or fix the JSON, then check the plan again.',
        'この後は「プレビュー」タブ（ゲーム詳細）で起動確認できます。': 'Next, you can verify the game boots from the "Preview" tab (game detail).',
        // 3D モデル
        '3D モデル（GLB）の実在状態': '3D model (GLB) file status',
        '「3Dキャラを自動生成」（Meshy）または手動作成の GLB を該当パスへ置くと「あり」になり、プレビューできます。': 'Once a GLB exists at the path — via "Auto-generate 3D character" (Meshy) or manual creation — it shows "Yes" and can be previewed.',
        '3Dキャラを自動生成': 'Auto-generate 3D character',
        'キャラ生成へ': 'Go to character generation',
        'キャラ生成:': 'Character generation:',
        'プレビュー': 'Preview',
        'ドラッグで回転・ホイールで拡大縮小。アニメーション付き GLB は先頭クリップを自動再生します。': 'Drag to rotate, scroll to zoom. Animated GLBs auto-play their first clip.',
        '種別GLB パス状態': 'TypeGLB pathStatus',
        'プロジェクト': 'Project',
        // API設定
        '本ツールの LLM 解析・JSON 生成・画像生成は OpenRouter、3D キャラ生成は Meshy の API を、利用者自身が発行したキーで直接呼び出します（実費はそれぞれのアカウントに課金。提供元が用意したデフォルトキーはありません）。': 'This tool calls OpenRouter (LLM analysis, JSON, image generation) and Meshy (3D character generation) directly with API keys you issue yourself. Costs are billed to your own accounts; no default keys are provided.',
        '⚠ キーはこのブラウザ（localStorage）にのみ保存されます。サーバやファイルには保存されません。共有PCでは利用しないでください。': '⚠ Keys are stored only in this browser (localStorage) — never on a server or in files. Do not use on a shared PC.',
        'OpenRouter（LLM 解析・JSON 生成・画像生成）': 'OpenRouter (LLM analysis / JSON / image generation)',
        'Meshy（3D キャラ自動生成）': 'Meshy (automatic 3D character generation)',
        '状態:': 'Status:',
        '✔ 設定済み・接続テスト済み': '✔ Saved & connection tested',
        '⚠ 設定済み・接続テスト未実施': '⚠ Saved, connection not tested',
        '）— 下の「接続テスト」を実行してください': ') — run "Connection test" below',
        '✖ 未設定': '✖ Not set',
        '保存先: このブラウザの localStorage': 'Stored in: this browser\'s localStorage',
        'キーの発行:': 'Get a key:',
        'API キー（ご自身で発行したもの）': 'API key (issued by you)',
        'キーを削除': 'Delete key',
        '接続テスト': 'Connection test',
        'OpenRouter 使用モデル': 'OpenRouter Models',
        'OpenRouterのモデルID（例:': 'Enter an OpenRouter model ID (example:',
        '）を直接指定します。設定とコスト情報はこのブラウザに保存されます。': ') directly. Settings and cost information are stored in this browser.',
        '文書解析': 'Document analysis',
        '物語解析・画像プロンプト・ワールド設計に使用': 'Used for story analysis, image prompts, and world design',
        'JSON生成': 'JSON generation',
        'マップ・NPC等のJSON生成と英訳に使用': 'Used for map/NPC JSON generation and English translation',
        '画像生成：コスト重視': 'Image generation: lowest cost',
        '画像を1枚ずつ生成する最安候補': 'Lowest-cost candidate for generating individual images',
        '画像生成：速度重視': 'Image generation: fastest',
        '生成時間を優先する候補（既定の使用候補）': 'Candidate prioritizing generation speed (default active candidate)',
        '画像生成：アダルト緩和重視': 'Image generation: relaxed adult-content rules',
        'アダルト系表現の規制が比較的緩い候補': 'Candidate with comparatively relaxed adult-content restrictions',
        'コスト重視': 'Lowest cost',
        '速度重視': 'Fastest',
        'アダルト緩和重視': 'Relaxed adult-content rules',
        'デフォルトに戻す': 'Restore default',
        '保存するとOpenRouterからI/O単価を取得します。': 'Save to retrieve I/O pricing from OpenRouter.',
        'I/O単価（OpenRouter API原値）': 'I/O pricing (raw OpenRouter API values)',
        '画像1枚あたりの生成コスト（推定）:': 'Estimated generation cost per image:',
        '画像1枚あたりの生成時間（推定）:': 'Estimated generation time per image:',
        '推定値未登録': 'Estimate unavailable',
        '秒': ' sec',
        '入力（prompt）:': 'Input (prompt):',
        '出力（completion）:': 'Output (completion):',
        '画像入力（image）:': 'Image input (image):',
        '画像出力（image_output）:': 'Image output (image_output):',
        'キャッシュ書込:': 'Cache write:',
        'リクエスト:': 'Request:',
        '内部推論:': 'Internal reasoning:',
        '単価情報なし': 'No pricing data',
        'キャラ生成で実際に使用する画像モデル': 'Image model actually used for character generation',
        'ここで選んだ候補を「キャラ生成」画面の既定値と実行モデルにします。': 'The candidate selected here becomes the default and execution model on the Character Generation screen.',
        '現在の使用モデル:': 'Current active model:',
        'OpenRouterからモデル単価を取得しています…': 'Retrieving model pricing from OpenRouter…',
        'モデルIDは author/model-id 形式で入力してください。': 'Enter the model ID in author/model-id format.',
        '例: deepseek/deepseek-v4-flash-0731': 'Example: deepseek/deepseek-v4-flash-0731',
        'モデルIDが不正です（例: deepseek/deepseek-v4-flash-0731）': 'The model ID is invalid (example: deepseek/deepseek-v4-flash-0731).',
        'モデルを保存できません:': 'Could not save the model:',
        'デフォルトに戻しましたが、OpenRouterから単価を取得できませんでした:': 'Restored the default, but could not retrieve pricing from OpenRouter:',
        '使用モデルは保存しましたが、OpenRouterから単価を取得できませんでした:': 'Saved the active model, but could not retrieve pricing from OpenRouter:',
        'デフォルトの使用候補に戻しましたが、OpenRouterから単価を取得できませんでした:': 'Restored the default active candidate, but could not retrieve pricing from OpenRouter:',
        'キーの表示/非表示を切り替えます（入力ミスの目視確認用）': 'Toggle key visibility (to visually check for paste mistakes)',
        'このブラウザに保存されているキーを削除しますか？': 'Delete the key stored in this browser?',
        'キーを入力してください': 'Enter a key.',
        'キーが短すぎます（コピーミスの可能性）': 'The key is too short (possibly a copy mistake).',
        'キーをこのブラウザに保存しました。続けて「接続テスト」で確認してください。': 'Key saved in this browser. Next, verify it with "Connection test".',
        '（⚠ 共有PCでは利用しないでください）': '(⚠ Do not use on a shared PC)',
        'キーが未設定です。キーを入力（または保存）してから接続テストしてください。': 'No key set. Enter (or save) a key before running the connection test.',
        '上限なしキー': 'Key without a limit',
        '疎通 OK（タスク一覧 API に接続できました）': 'Connection OK (reached the task list API)',
        'OpenRouter API キーの作成方法:': 'How to create an OpenRouter API key:',
        'Meshy API キーの作成方法:': 'How to create a Meshy API key:',
        // 認証
        '本ツールの利用にはログインが必要です。作成したゲームの公開・管理をアカウントに紐付けて安全に行うためです。': 'Sign-in is required to use this tool, so that publishing and managing your games is safely tied to your account.',
        'ログアウトしました。': 'Signed out.',
        'セッションの有効期限が切れました。もう一度ログインしてください。': 'Your session has expired. Please sign in again.',
        'メールアドレス': 'Email address',
        'パスワード': 'Password',
        'パスワード（確認）': 'Password (confirm)',
        '新しいパスワード': 'New password',
        '新しいパスワード（確認）': 'New password (confirm)',
        '8文字以上': '8+ characters',
        'パスワードをお忘れの方': 'Forgot your password?',
        'アカウントをお持ちでない方': 'No account yet?',
        'すでにアカウントをお持ちの方': 'Already have an account?',
        '新規ユーザー登録': 'Create account',
        'ログインへ戻る': 'Back to sign in',
        'Google でログイン（準備中）': 'Sign in with Google (coming soon)',
        'Apple でログイン（準備中）': 'Sign in with Apple (coming soon)',
        'Google 認証は現在準備中です': 'Google authentication is coming soon',
        'Apple 認証は現在準備中です': 'Apple authentication is coming soon',
        '登録する': 'Sign up',
        '登録したメールアドレス宛に確認メールが届きます。メール内のリンクを開くと登録が完了します。': 'A confirmation email will be sent to your address. Open the link inside to complete registration.',
        'パスワード再設定': 'Password reset',
        '登録済みのメールアドレスを入力してください。再設定用のリンクをお送りします。': 'Enter your registered email address. We will send you a reset link.',
        '再設定メールを送信': 'Send reset email',
        '新しいパスワードの設定': 'Set a new password',
        'パスワードを更新': 'Update password',
        '再設定リンクが無効または期限切れです。もう一度最初から操作してください。': 'The reset link is invalid or expired. Please start over.',
        '再設定メールを送り直す': 'Resend the reset email',
        'ログイン処理': 'Signing in',
        'ログイン処理を進めています。しばらくお待ちください……': 'Completing sign-in. Please wait…',
        '画面が切り替わらない場合はこちら': 'Click here if the screen does not change',
        'ログインを完了できませんでした。しばらくしてから再度お試しください。': 'Could not complete sign-in. Please try again later.',
        'アカウント': 'Account',
        '登録情報': 'Registration info',
        'メールアドレス:': 'Email:',
        'ログイン方法:': 'Sign-in method:',
        'メール': 'Email',
        '登録日時:': 'Registered:',
        '最終ログイン:': 'Last sign-in:',
        '（確認済み）': '(verified)',
        '（未確認: 確認メールのリンクを開いてください）': '(unverified: open the link in the confirmation email)',
        'プロフィール': 'Profile',
        '表示名（1〜60文字）': 'Display name (1–60 characters)',
        'アバター URL（任意・https:// のみ）': 'Avatar URL (optional, https:// only)',
        'アバター画像のプレビュー': 'Avatar image preview',
        'URLを入力すると画像を確認できます。': 'Enter a URL to preview the image.',
        '有効な https:// URLを入力してください。': 'Enter a valid https:// URL.',
        '画像を確認中…': 'Loading image preview…',
        'アバター画像を確認できます。': 'Avatar image preview is ready.',
        '画像を読み込めません。URLを確認してください。': 'Could not load the image. Check the URL.',
        'プロフィールを保存': 'Save profile',
        'セキュリティ': 'Security',
        'パスワードを変更する場合は、登録メールアドレス宛に再設定用リンクを送ります（送信前に確認ダイアログが表示されます）。': 'To change your password, we send a reset link to your registered email (a confirmation dialog appears before sending).',
        'パスワード再設定メールを送信': 'Send password reset email',
        'このブラウザからログアウトします（このブラウザ内の作業データや API キー設定は消えません）。': 'Signs out of this browser (work data and API key settings in this browser are kept).',
        'ログアウト': 'Sign out',
        '利用規約・公開ポリシー': 'Terms & Publishing Policies',
        // プロジェクトの保存（エクスポート）・取り込み（インポート）
        'プロジェクトの保存・取り込み': 'Save & import projects',
        '取り込む内容': 'What will be imported',
        '取り込み方法': 'Import method',
        'ゲームID': 'Game ID',
        '容量': 'Size',
        '保存日時': 'Saved at',
        '物語・解析': 'Story & analysis',
        'ゲーム本体': 'Game data',
        '3Dモデル': '3D models',
        '新しいゲームID': 'New game ID',
        '新しいタイトル': 'New title',
        '特集順位': 'Featured rank',
        '復元して取り込む': 'Restore',
        '元と同じプロジェクトとして戻します': 'Brings it back as the same project',
        '別ゲームとして複製する': 'Duplicate as another game',
        '似た内容の新しいゲームを作ります': 'Creates a similar new game',
        '英小文字はじまり・英数字/_/-': 'Lowercase letter first; letters, digits, _ and -',
        '任意・空欄なら元のまま': 'Optional; keeps the original when empty',
        '空欄で解除': 'Leave empty to clear',
        '（未設定）': '(not set)',
        '⬆ ファイルから取り込み': '⬆ Import from file',
        '⬇ ファイルに保存': '⬇ Save to file',
        'ファイルを作成しています…（完了までお待ちください）': 'Building the file… (please wait)',
        '取り込んでいます…（完了までお待ちください）': 'Importing… (please wait)',
        'ファイルを読み込んでいます……': 'Reading the file…',
        '読み込みに失敗しました': 'Failed to read the file',
        'このファイルには生成済み3Dモデルが含まれていません（取り込み後に3D生成が必要です）。': 'This file contains no generated 3D models (you will need to generate them after importing).',
        '同じプロジェクトがこのブラウザにあります。「復元」を選ぶと現在の内容が置き換わります。': 'The same project already exists in this browser. Choosing “Restore” replaces its current contents.',
        '取り込む': 'Import',
        '確認のうえ、このファイルの内容を取り込みます': 'Asks for confirmation, then imports the contents of this file',
        'やめる': 'Cancel',
        // 「ファイルに保存」の実行前確認ダイアログ
        'ファイルに保存': 'Save to file',
        '含まれるもの: 物語・解析・プロンプト・ゲーム本体・生成画像・生成3Dモデル': 'Includes: story, analysis, prompts, game data, generated images and 3D models',
        'ブラウザのダウンロード先へ保存されます。': 'It will be saved to your browser’s download folder.',
        // 公開中の作品に対する操作制限（削除・プレビュー・①〜⑥の編集）
        '公開中の作品は削除できません。「マイ公開ゲーム」で公開を取り下げて完全に非公開へ戻すと削除できます': 'Published games cannot be deleted. Withdraw the publication in “My published games” to make it fully private, then it can be deleted',
        '公開中の作品はプレビューできません（公開ページで実際に遊べます）': 'Published games cannot be previewed (you can play the published page itself)',
        '公開中の作品は編集できません。「マイ公開ゲーム」で公開を取り下げて完全に非公開へ戻すと編集できます': 'Published games cannot be edited. Withdraw the publication in “My published games” to make it fully private, then it can be edited',
        '公開中のため編集できません': 'Editing is disabled while published',
        '公開中の作品は ① 解析 〜 ⑥ JSON設計 の内容を変更できません。編集するには公開を取り下げて、完全に非公開の状態へ戻してください。': 'While a game is published, ① Analysis through ⑥ JSON design cannot be changed. Withdraw the publication and return it to a fully private state to edit it.',
        'マイ公開ゲームで取り下げる': 'Withdraw in My published games',
        '公開状態の変更・公開取り下げを行う「マイ公開ゲーム」画面へ移動します': 'Opens “My published games”, where you can change visibility or withdraw the publication',
        '⑦ 公開状態を見る': '⑦ View publish status',
        'このプロジェクトの公開状態を確認します': 'Shows the publish status of this project',
        // 一時停止中はギャラリー掲載を申請させない（掲載されても遊べないため）
        '一時停止中はギャラリー掲載を申請できません（掲載されても遊べないため）。先に「マイ公開ゲーム」で公開状態を「限定公開（URLを知っている人のみ）」へ戻してください。': 'You cannot request a gallery listing while publishing is paused (nobody could play it). First set the visibility back to “Unlisted (only people with the URL)” in “My published games”.',
        '一時停止中は申請できません。「マイ公開ゲーム」で公開状態を「限定公開（URLを知っている人のみ）」へ戻すと申請できます': 'Requests are unavailable while publishing is paused. Set the visibility back to “Unlisted (only people with the URL)” in “My published games” to request a listing',
        // ⑦ 公開・マイ公開ゲーム（Phase D-1）
        // ホームの「ゲーム」欄の容量ツールチップ（容量の基準は全画面で 1 つ＝公開したときに使う容量）
        '公開したときに使う容量（ゲームデータ・キャスト・開始画面素材・生成3Dモデル）です。「⑦ 限定公開」「マイ公開ゲーム」に出る容量と同じ基準で、物語の原文や解析結果などの作業データは含みません': 'The size this game uses when published (game data, cast, start-screen art, and generated 3D models). It is measured the same way as the size shown in “⑦ Unlisted publish” and “My published games”, and excludes working data such as the original story text and analysis results.',
        'マイ公開ゲーム': 'My published games',
        '⑦ 限定公開': '⑦ Unlisted publish',
        '⑦ 限定公開へ': 'Go to ⑦ Unlisted publish',
        '⑦ 公開設定': '⑦ Publish settings',
        // 前工程が未完の画面から、その工程へ入るボタン
        '① 解析へ': 'Go to ① Analysis',
        '③ キャラ生成へ': 'Go to ③ Characters',
        '④ ワールド設計へ': 'Go to ④ World design',
        '⑤ 開始画面設計へ': 'Go to ⑤ Start screen',
        '⑥ JSON設計へ': 'Go to ⑥ JSON design',
        '① 解析の画面を開きます': 'Opens the ① Analysis screen',
        '② ベースデータ生成の画面を開きます': 'Opens the ② Base data screen',
        '③ キャラ生成の画面を開きます': 'Opens the ③ Characters screen',
        '④ ワールド設計の画面を開きます': 'Opens the ④ World design screen',
        '⑤ 開始画面設計の画面を開きます': 'Opens the ⑤ Start screen design screen',
        '⑥ JSON設計の画面を開きます': 'Opens the ⑥ JSON design screen',
        'このゲームを限定公開（URLを知っている人だけが遊べる状態）にする画面へ移動します': 'Opens the screen to publish this game as unlisted (only people with the URL can play)',
        '▶ 公開ページ': '▶ Published page',
        '公開機能はこの環境では利用できません。': 'Publishing is not available in this environment.',
        'ゲームの公開にはログインが必要です。公開したゲームをアカウントに紐付けて安全に管理するためです。': 'Sign-in is required to publish games, so that your published games are safely tied to your account.',
        '公開情報を読み込み中……': 'Loading publishing info…',
        '公開の準備に失敗しました': 'Failed to prepare publishing',
        '公開パッケージ': 'Publish package',
        '公開の流れ': 'How publishing works',
        'ここで実行すると 限定公開（URLを知っている人だけが遊べます。ギャラリーには出ません）': 'Running this here makes it Unlisted (only people with the URL can play; it is not shown in the gallery)',
        '「マイ公開ゲーム」から ギャラリー掲載を申請': 'Request a gallery listing from “My published games”',
        '運営の審査に通ると 一般公開（ギャラリーに掲載されます）': 'Once it passes review, it becomes Public (listed in the gallery)',
        '🎉 限定公開が完了しました（URLを知っている人だけが遊べます）': '🎉 Published as unlisted (only people with the URL can play)',
        'ギャラリーに載せるには、次に「マイ公開ゲーム」から掲載申請を行い、運営の審査を通過する必要があります。': 'To appear in the gallery, request a listing from “My published games” and pass the review.',
        '公開URL（gameId）:': 'Public URL (gameId):',
        '初回公開は限定公開（unlisted）で確定します。URLを知っている人だけが遊べます。': 'The first publish is always unlisted: only people who know the URL can play.',
        'クォータ情報を取得できませんでした:': 'Could not load quota info:',
        '公開情報': 'Publishing details',
        'タイトル（日本語・1〜120文字）': 'Title (Japanese, 1–120 characters)',
        '紹介文（任意・ギャラリーと共有リンクに表示・300文字まで）': 'Summary (optional, shown in the gallery and link previews, up to 300 characters)',
        'どんな物語のゲームかを紹介しましょう（先頭100文字ほどが一覧やSNSの要約に出ます）': 'Introduce what kind of story your game tells (about the first 100 characters appear in listings and social previews)',
        '規約への同意': 'Consent to the terms',
        '公開には次の文書への同意が必要です（同意記録は文書の版数つきで保存されます）。': 'Publishing requires consent to the following documents (consent is recorded with the document version).',
        'に同意する': 'I agree to it',
        '公開の実行': 'Publish',
        '🚀 ゲームを公開する': '🚀 Publish game',
        '実行ログがここに表示されます': 'The execution log appears here',
        'タイトル（日本語）は 1〜120文字で入力してください。': 'Enter a Japanese title between 1 and 120 characters.',
        '公開には全ての文書への同意が必要です。': 'Publishing requires consent to all documents.',
        'ゲームを公開しますか？': 'Publish this game?',
        '初回公開は限定公開（URLを知っている人のみ）で確定します。': 'The first publish is unlisted (only people with the URL).',
        '🎉 公開が完了しました！': '🎉 Published!',
        '▶ 公開ページを開く': '▶ Open published page',
        'URLをコピー': 'Copy URL',
        'この URL をコピーしてください': 'Copy this URL',
        'マイ公開ゲームで管理': 'Manage in My published games',
        '公開に失敗しました。ログを確認してください。': 'Publishing failed. Check the log.',
        '一覧を取得できませんでした': 'Could not load the list',
        '公開中のゲームはまだありません。プロジェクトの「⑦ 限定公開」タブから公開できます。': 'No published games yet. You can publish from the “⑦ Unlisted publish” tab of a project.',
        '公開中のゲームはまだありません。まず物語プロジェクトを作り、②（ベースデータ生成）まで進めると公開できます。': 'No published games yet. Create a story project first and complete step ② (base data) to be able to publish.',
        'ホーム（プロジェクト一覧）へ': 'Go to Home (projects)',
        '作成中のプロジェクト一覧（ホーム）へ移動します': 'Goes to the Home screen with your projects in progress',
        'このプロジェクトの「⑦ 限定公開」画面へ移動します': 'Goes to the “⑦ Unlisted publish” screen of this project',
        '公開状態を変更': 'Change visibility',
        'ギャラリー掲載を申請': 'Request gallery listing',
        '審査を取り下げる': 'Withdraw review request',
        '非公開のままではギャラリー掲載を申請できません（掲載されても遊べないため）。先に「限定公開」へ変更してください。': 'You cannot request a gallery listing while the game is Private (it would be listed but unplayable). Switch it to Unlisted first.',
        '運営により停止されています。解除は運営が行います（作成者側からの公開範囲の変更・更新公開・削除はできません）。通報・審査の結果をご確認ください。': 'Suspended by the operator. Only the operator can lift it (you cannot change visibility, publish updates or delete). Please check the report/review results.',
        '審査（ギャラリー掲載の申請）を取り下げ、審査前の状態へ戻します。取り下げると公開範囲の変更・更新公開・削除が再びできます。確認ダイアログが表示されます': 'Withdraws the gallery listing request and returns it to unreviewed. After withdrawing you can change visibility, publish updates and delete again. A confirmation dialog appears.',
        'ギャラリー掲載の審査中です。審査対象のデータが変わらないよう、審査が終わるまで公開範囲の変更・更新公開・削除はできません。': 'This game is under review for gallery listing. To keep the reviewed data unchanged, visibility changes, update publishing and deletion are disabled until the review finishes.',
        'このプロジェクトのゲームを games.voxelnovels.com へ限定公開します（URLを知っている人だけが遊べます）。確認ダイアログが表示されます': 'Publishes this project’s game to games.voxelnovels.com as unlisted (only people with the URL can play). A confirmation dialog appears.',
        '公開取り下げ': 'Withdraw publication',
        '▶ 遊ぶ': '▶ Play',
        '公開中のゲームを新しいタブで開いて実際に遊んで確認します': 'Opens the published game in a new tab so you can actually play and check it',
        '公開URLをコピーします。SNS やメッセージで共有できます': 'Copies the public URL so you can share it on social media or in messages',
        '問い合わせ・異議申立て': 'Contact / appeal',
        '通報・異議申立ての窓口（公開ポリシー）を新しいタブで開きます': 'Opens the report and appeal page (publishing policy) in a new tab',
        '② ベースデータ生成へ': 'Go to ② base data',
        '② ベースデータ生成の画面へ移動します（公開にはこの工程の完了が必要です）': 'Goes to the ② base data screen (publishing requires this step to be complete)',
        '工程の一覧へ戻る': 'Back to the step list',
        'このプロジェクトの工程一覧（①解析）へ戻ります': 'Goes back to this project’s step list (① analysis)',
        'マイ公開ゲームで枠を空ける': 'Free up space in My published games',
        '公開中のゲームを取り下げて容量・件数の枠を空ける画面へ移動します': 'Opens the screen where you can withdraw a published game to free up storage or slots',
        '容量・件数の上限を解消すると公開できます': 'Resolve the storage/slot limit to publish',
        '公開したゲームを新しいタブで開いて確認します': 'Opens the published game in a new tab to check it',
        '続けてギャラリーへの掲載を運営へ申請します。承認されると一般公開（ギャラリー掲載）になります。確認ダイアログが表示されます': 'Goes on to request a gallery listing from the operator. Once approved it becomes public (listed). A confirmation dialog appears.',
        '掲載申請に失敗しました': 'Failed to request the listing',
        '公開済み情報': 'Published info',
        '公開中の容量': 'Published size',
        '初回公開日時': 'First published',
        '内容を変更するには、公開を取り下げてから、もう一度公開してください（ギャラリー掲載の審査は最初からやり直しになります）。': 'To change the contents, withdraw the publication and publish again (the gallery review starts over).',
        'ギャラリー掲載の審査中です。審査が終わるまで公開の操作はできません（「マイ公開ゲーム」で審査状態の確認・取り下げができます）。': 'This game is under review for gallery listing. Publishing actions are disabled until it finishes (check or withdraw it in “My published games”).',
        '運営により停止されています。解除は運営が行います。': 'Suspended by the operator. Only the operator can lift it.',
        '公開を取り下げて公開URLを 404 にします（このブラウザ内の作成データは残るので、いつでも公開し直せます）。うっかり公開してしまった場合もここから戻せます。確認ダイアログが表示されます': 'Withdraws the publication so the URL returns 404 (your work data in this browser is kept, so you can publish again anytime). Use this if you published by mistake. A confirmation dialog appears.',
        '公開を取り下げて公開URLを 404 にします（このブラウザ内の作成データは残るので、いつでも公開し直せます）。ゲーム内容を変更したい場合も、いったん取り下げてから「⑦ 限定公開」で公開し直します（審査は最初からやり直しになります）。確認ダイアログが表示されます': 'Withdraws the publication so the URL returns 404 (your work data in this browser is kept, so you can publish again anytime). To change the game contents, withdraw it and publish again from “⑦ Unlisted publish” (the review starts over). A confirmation dialog appears.',
        '公開状態の変更・ギャラリー掲載の申請・公開取り下げをまとめて行う画面へ移動します': 'Opens the screen where you can change visibility, request a gallery listing, or withdraw the publication',
        '公開取り下げに失敗しました': 'Failed to withdraw the publication',
        '公開URL（gameId）': 'Public URL (gameId)',
        '対象ファイル': 'Files to publish',
        '生成3Dモデル': 'Generated 3D models',
        '限定公開の一時停止': 'Unlisted publishing paused',
        '限定公開（URLを知っている人のみ）': 'Unlisted (only people with the URL)',
        '一般公開': 'Public',
        '未申請': 'Not requested',
        '審査中': 'Under review',
        '承認済み': 'Approved',
        '要修正': 'Changes requested',
        '運営停止中': 'Suspended',
        '（要審査承認）': '(requires review approval)',
        '審査には日数がかかることがあります。承認されると一般公開（ギャラリー掲載）を選べるようになります。': 'Review may take several days. Once approved, you can switch the game to Public (gallery listing).',
        '公開URLは直ちに 404 になり、公開ファイルは削除されます。元に戻せません。': 'The public URL immediately returns 404 and the published files are deleted. This cannot be undone.',
        '（このブラウザ内の作成データは残るため、再公開は可能です）': '(Creation data in this browser is kept, so you can publish again.)',
        '審査:': 'Review:',
        '審査コメント:': 'Review note:',
        // 公開審査（管理者・Phase D-1）
        '公開審査': 'Review console',
        '選んだ公開範囲へ切り替えます（一般公開は審査承認済みのゲームだけ選べます）。確認ダイアログが表示されます': 'Switches to the selected visibility (Public is available only for approved games). A confirmation dialog appears.',
        'ギャラリーへの掲載を運営へ申請します。承認されると一般公開（ギャラリー掲載）になります。確認ダイアログが表示されます': 'Requests a gallery listing from the operator. Once approved it becomes Public (listed). A confirmation dialog appears.',
        '掲載申請中のゲームをそのまま一般公開（ギャラリー掲載）にします。確認ダイアログが表示されます': 'Makes a requested game public (listed in the gallery) right away. A confirmation dialog appears.',
        '却下して作成者へ差し戻します。審査コメントが作成者に表示されます。確認ダイアログが表示されます': 'Rejects it and returns it to the creator. Your review note is shown to them. A confirmation dialog appears.',
        '審査前（未申請）の状態へ戻します。作成者が再申請できるようになります。確認ダイアログが表示されます': 'Moves it back to unreviewed so the creator can request a listing again. A confirmation dialog appears.',
        '規約違反・通報対応として公開を停止します。プレイもギャラリー掲載も止まります。確認ダイアログが表示されます': 'Suspends the game for a policy violation or report. Play and gallery listing both stop. A confirmation dialog appears.',
        '公開ページを新しいタブで開き、起動・表示・内容を確認します': 'Opens the published page in a new tab to check that it launches and looks right',
        'ギャラリーの掲載一覧（index/home.json・gallery.json）を台帳から作り直します': 'Rebuilds the gallery listings (index/home.json and gallery.json) from the database',
        '公開中のギャラリーを新しいタブで開きます': 'Opens the live gallery in a new tab',
        '過去に審査したゲームの履歴（いつ・どのゲームを・どの状態にしたか）を表示します': 'Shows the history of past reviews (when, which game, and what it was set to)',
        '保存したファイル（.zip）を選び、内容を確認してから取り込みます。復元（元と同じプロジェクトに戻す）と複製（似た別ゲームを新規作成）を選べます': 'Pick a saved .zip, review its contents, then import it. You can restore it as the same project or duplicate it as a similar new game.',
        '作業データ一式（物語・解析・プロンプト・ゲーム本体・生成画像・生成3Dモデル）を 1つのファイルに保存します。バックアップ・別のパソコンや別サイトへの持ち込み・似た別ゲームの土台に使えます': 'Saves all of your work (story, analysis, prompts, game data, generated images and 3D models) into one file — for backup, moving to another computer or site, or as the base for a similar new game',
        '審査履歴を表示': 'Show review history',
        '審査履歴を閉じる': 'Hide review history',
        '審査履歴を取得できませんでした': 'Could not load the review history',
        '審査の履歴はまだありません。': 'No review history yet.',
        '公開状態': 'Visibility',
        '審査状態': 'Review status',
        '版（rev）': 'Revision',
        '審査コメント': 'Review note',
        '前回の審査コメント': 'Previous review note',
        '現在の特集順位': 'Current featured rank',
        '紹介文': 'Summary',
        '使用中の容量': 'Storage used',
        '公開中のゲーム数': 'Published games',
        '1ゲームの上限': 'Limit per game',
        '無制限': 'Unlimited',
        '申請中': 'Pending',
        '公開ゲーム総数': 'Published games total',
        '日時': 'Date',
        'ゲーム': 'Game',
        '変更': 'Change',
        '特集': 'Featured',
        '却下・停止では必須。作成者の画面に表示されます': 'Required for rejection/suspension; shown to the creator',
        'ホーム画面へ戻ります': 'Goes back to the Home screen',
        '審査キュー': 'Review queue',
        '審査キューを取得できませんでした': 'Could not load the review queue',
        'この画面は管理者アカウントでのみ利用できます。': 'This screen is available to administrator accounts only.',
        'ホームへ戻る': 'Back to Home',
        '件数が上限に達したため一部のみ表示しています。': 'Only part of the list is shown because the limit was reached.',
        'ギャラリーを再生成': 'Rebuild gallery',
        '確認のうえ、ギャラリーの掲載一覧を作り直します': 'Asks for confirmation, then rebuilds the gallery listing',
        'ギャラリーを開く': 'Open gallery',
        '再生成に失敗しました': 'Rebuild failed',
        '審査待ちの申請はありません。': 'No listing requests are waiting for review.',
        'まだ公開ゲームがありません。': 'There are no published games yet.',
        '▶ ゲームを確認': '▶ Check the game',
        '特集順位:': 'Featured rank:',
        '承認する': 'Approve',
        '却下する': 'Reject',
        '審査前に戻す': 'Move back to unreviewed',
        '公開を停止する': 'Suspend',
        '却下・停止には理由（審査コメント）が必要です。': 'Rejection and suspension require a reason (review note).',
        '審査結果を反映できませんでした': 'Could not apply the review result',
        'アカウントの削除': 'Delete account',
        'アカウントと登録情報（プロフィール）を完全に削除します。この操作は取り消せません。削除完了後、登録メールアドレスへお知らせを送信します（このブラウザ内の作業データや API キー設定は消えません）。': 'Permanently deletes your account and registered profile. This cannot be undone. After deletion, a notice is sent to your registered email address (work data and API key settings in this browser are kept).',
        'アカウントを削除': 'Delete account',
        '確認のうえ、アカウントを完全に削除します': 'Asks for confirmation, then permanently deletes your account',
        'アカウントを削除しますか？': 'Delete your account?',
        'アカウントと登録情報（プロフィール）は完全に削除され、元に戻せません。': 'Your account and registered profile will be permanently deleted and cannot be restored.',
        '（このブラウザ内の作業データや API キー設定は消えません）': '(Work data and API key settings in this browser are kept.)',
        'アカウントを削除しています……': 'Deleting your account…',
        'アカウントを削除しました。ご利用ありがとうございました。': 'Your account has been deleted. Thank you for using VoxelNovels.',
        'アカウントの削除に失敗しました。時間をおいて再試行してください。': 'Failed to delete the account. Please try again later.',
        '認証情報がありません。再ログインしてください。': 'Missing credentials. Please sign in again.',
        'セッションが無効です。再ログインしてください。': 'Your session is invalid. Please sign in again.',
        'ユーザーを特定できませんでした。再ログインしてください。': 'Could not identify the user. Please sign in again.',
        'サーバ設定が不足しています。時間をおいて再試行してください。': 'Server configuration is incomplete. Please try again later.',
        'アカウント削除処理でエラーが発生しました。時間をおいて再試行してください。': 'An error occurred while deleting the account. Please try again later.',
        '送信しました。': 'Sent.',
        '処理を完了できませんでした。': 'The operation could not be completed.',
        'アカウント機能はこの環境では利用できません。': 'Account features are not available in this environment.',
        'パスワードの表示/非表示を切り替えます': 'Toggle password visibility',
        // 確認ダイアログ（固定文）
        '物語解析を実行しますか？（LLM 実費が発生します）': 'Run story analysis? (LLM cost applies)',
        '再解析を実行しますか？': 'Re-run analysis?',
        '現在の解析結果（画面で編集した内容を含む）は上書きされます。LLM 実費が発生します。': 'The current analysis (including on-screen edits) will be overwritten. LLM cost applies.',
        '現在の解析結果は削除され、再解析が必要になります。': 'The current analysis will be deleted and must be run again.',
        '⚠ この操作を実行すると、整合性を保つため②以降の工程データはすべて初期化されます。': '⚠ To keep data consistent, this operation resets all data from step ② onward.',
        '⚠ この操作を実行すると、整合性を保つため③以降の工程データはすべて初期化されます。': '⚠ To keep data consistent, this operation resets all data from step ③ onward.',
        '⚠ この操作を実行すると、整合性を保つため④以降の工程データ（⑤ 開始画面設計を含む）はすべて初期化されます。': '⚠ To keep data consistent, this operation resets all data from step ④ onward (including ⑤ start screen design).',
        '⚠ この操作を実行すると、整合性を保つため⑤ 開始画面設計と⑥-1以降の工程データはすべて初期化されます。': '⚠ To keep data consistent, this operation resets ⑤ start screen design and all data from step ⑥-1 onward.',
        '⚠ この操作を実行すると、整合性を保つため⑥-2以降の工程データはすべて初期化されます。': '⚠ To keep data consistent, this operation resets all data from step ⑥-2 onward.',
        '⚠ この操作を実行すると、整合性を保つため⑥-3の取り込み結果は初期化されます。': '⚠ To keep data consistent, this operation resets the step ⑥-3 import result.',
        '全キャストの画像プロンプトを生成しますか？（LLM 実費が発生します）': 'Generate image prompts for all casts? (LLM cost applies)',
        '全キャストの画像プロンプトを再生成しますか？': 'Regenerate image prompts for all casts?',
        '既存のプロンプトは上書きされます。LLM 実費が発生します。': 'Existing prompts will be overwritten. LLM cost applies.',
        'ワールド設計を実行しますか？（LLM 実費が発生します）': 'Run world design? (LLM cost applies)',
        'ワールド設計を再実行しますか？': 'Re-run world design?',
        '現在の設計（編集内容を含む）は上書きされます。LLM 実費が発生します。': 'The current design (including edits) will be overwritten. LLM cost applies.',
        '解析結果を上書き保存しますか？': 'Overwrite and save the analysis?',
        'ワールド設計を上書き保存しますか？': 'Overwrite and save the world design?',
        '依頼文を保存しますか？': 'Save the request text?',
        '（保存した内容が ⑥-2 の LLM 実行に使われます）': '(The saved text will be used for the ⑥-2 LLM run)',
        '依頼文を生成しますか？（解析・設計の内容から既定の依頼文を作成します）': 'Generate the request text? (Creates a default from the analysis and design)',
        '依頼文を再生成しますか？': 'Regenerate the request text?',
        '画面・保存済みの編集内容は破棄され、既定の内容で作り直されます。': 'On-screen and saved edits will be discarded and replaced with the default.',
        '② ゲーム生成を実行しますか？': 'Run ② Game generation?',
        '（ドライラン内容のとおり、ゲーム環境一式を新規作成します）': '(Creates the full game environment as shown in the dry run)',
        '② ベースデータを生成しますか？': 'Generate ② Base data?',
        '後続工程の土台となるキャラ設定・共有データ・開始ワールドを作成します。': 'Creates the character settings, shared data, and starting world used as the base for later steps.',
        'この内容で新規ゲーム環境を作成しますか？': 'Create a new game environment with these settings?',
        '生成済み JSON をゲームへ取り込み、検証まで実行しますか？': 'Import the generated JSON into the game and run validation?',
        '（プレースホルダのマップ等は生成内容で上書きされます）': '(Placeholder maps etc. will be overwritten with the generated content)',
        '生成された内容をゲームデータへ配置しますか？': 'Place the generated content into the game data?',
        '（ドライラン結果のとおり作成・上書き・マージされます）': '(Files are created / overwritten / merged as shown in the dry run)',
        'キャラクター設定の変更を実行しますか？': 'Apply the character configuration change?',
        '（ドライランで確認した内容でゲームデータを書き換えます）': '(Rewrites game data as confirmed in the dry run)',
        'ワールドの追加を実行しますか？': 'Add the world?',
        '（ドライランで確認した内容でゲームデータへマップ一式を追加します）': '(Adds the map set to the game data as confirmed in the dry run)',
        'npm ci を実行しますか？': 'Run npm ci?',
        '（依存パッケージのクリーンインストール・数分かかります）': '(Clean-installs dependencies; takes a few minutes)',
        'generate:treasures を実行しますか？': 'Run generate:treasures?',
        '（宝箱データを再生成して上書きします）': '(Regenerates and overwrites treasure data)',
        'dataCache.version を更新しますか？': 'Bump dataCache.version?',
        '（マップ設定 JSON の版番を書き換えます）': '(Rewrites the version number in map JSON)',
        '本番ビルドを実行しますか？（数分かかる場合があります）': 'Run the production build? (May take several minutes)',
        'パスワードを更新しますか？': 'Update your password?',
        'プロフィールを保存しますか？': 'Save your profile?',
        'ログアウトしますか？': 'Sign out?',
        '処理を中断しますか？': 'Cancel this operation?',
        '安全な区切りまで進めてから停止します（それまでの途中経過は保存されない場合があります）。': 'It will stop at the next safe point (progress up to then may not be saved).',
        'この処理は中断できません（まもなく完了します）。': 'This operation cannot be canceled (it will finish shortly).',
        'npm ci は依存関係の破損を避けるため中断できません。完了までお待ちください。': 'npm ci cannot be canceled to avoid corrupting dependencies. Please wait for it to finish.',
        '処理を中断しました。': 'Operation canceled.',
        '安全な区切りで停止しました（途中経過は保存されていない場合があります）。': 'Stopped at a safe point (progress may not have been saved).',
        '未完了の工程はありません（取り込みまで完了済みです）。': 'No remaining steps (everything through import is complete).',
        '残りの工程を一括実行します:': 'Batch-run the remaining steps:',
        'LLM 実費の目安: 合計 ≒$0.02〜0.1（物語の長さで変動。キャスト画像・3D の自動生成は含まれません）': 'Estimated LLM cost: ≈$0.02–0.1 total (varies with story length; cast image / 3D generation not included)',
        '途中で止めたいときは ESC キーで中断できます。実行しますか？': 'You can cancel anytime with the ESC key. Proceed?',
        '🎉 一括実行が完了しました。プレビューで動作を確認してください。': '🎉 Batch run complete. Check the game in the preview.',
        'OpenRouter の API キーが未設定か、接続テストが完了していません。': 'Your OpenRouter API key is not set, or the connection test has not passed.',
        'Meshy の API キーが未設定か、接続テストが完了していません。': 'Your Meshy API key is not set, or the connection test has not passed.',
        '先に「API設定」画面でキーを保存し、「接続テスト」を実行してください。': 'First save your key in "API Settings" and run "Connection test".',
        '（このあと API設定画面を開きます）': '(API Settings will open next)',
        'サーバに接続できません。処理はサーバ側で継続している可能性があります。': 'Cannot reach the server. The job may still be running on the server.',
        '再接続を試し続けますか？': 'Keep trying to reconnect?',
        '（キャンセルすると画面のロックを解除します。処理自体は中断されません。サーバの起動を確認してからページを再読み込みしてください）': '(Cancel to unlock the screen. The job itself is not canceled. Check the server and reload the page.)',
        // エラーメッセージ
        'ジョブ取得に失敗': 'Failed to fetch the job',
        'サーバとの接続が切れたため、画面での追跡を中止しました。処理はサーバ側で継続または完了している可能性があります。サーバ起動を確認し、ページを再読み込みして状態を確認してください。': 'Lost connection to the server, so on-screen tracking stopped. The job may still be running or already finished. Check the server, then reload the page to see the result.',
        'メールアドレスの形式を確認してください。': 'Please check the email address format.',
        'パスワードは 8文字以上で入力してください。': 'Password must be at least 8 characters.',
        'パスワードが長すぎます（72文字以内）。': 'Password is too long (max 72 characters).',
        'パスワード（確認）が一致しません。': 'Passwords do not match.',
        'ログインに失敗しました。メールアドレスまたはパスワードを確認してください。': 'Sign-in failed. Check your email address and password.',
        'メールアドレスの確認が完了していません。登録時に届いた確認メールのリンクを開いてください。': 'Your email is not verified yet. Open the link in the confirmation email you received.',
        '登録処理を完了できませんでした。入力内容を確認して、しばらくしてから再度お試しください。': 'Registration could not be completed. Check your input and try again later.',
        '操作が続けて行われたため制限されています。しばらく待ってから再度お試しください。': 'Rate limited due to repeated requests. Please wait a moment and try again.',
        '認証リンクが無効または期限切れです。もう一度最初から操作してください。': 'The link is invalid or expired. Please start over.',
        'セッションの有効期限が切れました。もう一度ログインしてください。 ': 'Your session has expired. Please sign in again.',
        '通信に失敗しました。ネットワーク接続を確認して再度お試しください。': 'Network error. Check your connection and try again.',
        '処理を完了できませんでした。しばらくしてから再度お試しください。': 'The operation could not be completed. Please try again later.',
        '確認メールを送信しました。メール内のリンクを開いて登録を完了してください。': 'Confirmation email sent. Open the link inside to complete registration.',
        '入力されたメールアドレス宛に、パスワード再設定の案内を送信しました（登録がある場合）。': 'If the address is registered, a password reset email has been sent to it.',
        'パスワードを更新しました。': 'Password updated.',
        '表示名は 1〜60文字で入力してください。': 'Display name must be 1–60 characters.',
        'アバター URL は https:// で始まる必要があります。': 'Avatar URL must start with https://.',
        'アバター URL が長すぎます。': 'Avatar URL is too long.',
        '変更内容がありません。': 'Nothing to save.',
        'ログインが必要です。': 'Sign-in required.',
        '不明なログイン方法です。': 'Unknown sign-in method.',
        'プロフィールを保存しました。': 'Profile saved.',
        'エントリが見つかりません。': 'No entries found.',
        'エラーを含むため、どのファイルも配置しません。入力を修正して再解析してください。': 'Errors found — no files will be placed. Fix the input and re-analyze.',
        '既存ファイルの上書き/マージが含まれます。「② 配置を実行」の前に「既存ファイルの上書きを許可」へチェックを入れてください。': 'Includes overwrites/merges of existing files. Check "Allow overwriting existing files" before "② Place files".',
        '画像が大きすぎます（上限 25MB）': 'Image is too large (max 25MB).',
        '画像の差し替えに失敗しました': 'Failed to replace the image.',
        '画像生成の開始に失敗しました': 'Failed to start image generation.',
        'プロンプト再生成の開始に失敗しました': 'Failed to start prompt regeneration.',
        '3D生成の開始に失敗しました': 'Failed to start 3D generation.',
        '取り込みに失敗しました': 'Import failed.',
        // F2 取り込み・ゲーム詳細タブ
        '取り込み': 'Import',
        '検証': 'Validate',
        'モデル': 'Models',
        '生成・版番': 'Generate & version',
        '公開準備': 'Publish prep',
        'キャラ設定': 'Characters',
        'ワールド追加': 'Add world',
        '① 解析（ドライラン）': '① Analyze (dry run)',
        '既存ファイルの上書きを許可': 'Allow overwriting existing files',
        '② 配置を実行': '② Place files',
        '上書き': 'Overwrite',
        'マージ': 'Merge',
        '新規': 'New',
        '✔ 作成': '✔ Created',
        '✔ 上書き': '✔ Overwritten',
        '✔ マージ': '✔ Merged',
        '― 見送り': '— Skipped',
        '✖ 拒否': '✖ Rejected',
        '配置先動作スキーマ差分（トップキー）状態': 'DestinationActionSchemaDiff (top keys)Status',
        '検証（F3 スキーマ / F4 整合）': 'Validation (F3 schema / F4 integrity)',
        'validate:maps 実行': 'Run validate:maps',
        '整合チェック実行': 'Run integrity check',
        'すべて実行（schema + 整合 + 翻訳 + アセット）': 'Run all (schema + integrity + i18n + assets)',
        '問題は見つかりませんでした。': 'No issues found.',
        '種別ルールファイルJSON パス内容': 'LevelRuleFileJSON pathDetails',
        'エラー': 'Error',
        '警告': 'Warning',
        'ヒント:': 'Hint:',
        'ローカル assets/ を確認': 'Check local assets/',
        '本番 CDN も確認（HEAD）': 'Also check production CDN (HEAD)',
        '起動': 'Start',
        '停止': 'Stop',
        '（未起動）': '(not running)',
        '本番ビルド実行': 'Run production build',
        '公開可否を確認': 'Check publish readiness',
        '予定 URL:': 'Planned URL:',
        '公開チェックリスト': 'Publish checklist',
        '注意': 'Notes',
        'キャラ設定変更（F11）': 'Character configuration (F11)',
        'モード': 'Mode',
        '表示名・説明変更（いつでも可）': 'Rename / description (anytime)',
        'ID 変更（公開前限定）': 'Change ID (before publishing only)',
        'モデル差し替え': 'Swap model',
        '対象キャラ': 'Target character',
        '① ドライラン': '① Dry run',
        '② 実行': '② Run',
        '表示名（ja）': 'Display name (ja)',
        '表示名（en）': 'Display name (en)',
        '説明（ja・任意）': 'Description (ja, optional)',
        '説明（en・任意）': 'Description (en, optional)',
        'ワールド追加（F12）': 'Add world (F12)',
        'タイプ': 'Type',
        '通常ワールド（都市・フィールド）': 'Normal world (city / field)',
        'ダンジョン型（labyrinth_dungeon 相当）': 'Dungeon type (like labyrinth_dungeon)',
        '無限宝物庫型（mapId は eternal_vault 固定）': 'Infinite vault type (mapId fixed to eternal_vault)',
        'マップ名（ja）': 'Map name (ja)',
        'マップ名（en）': 'Map name (en)',
        '接続元ワールド（通常ワールドでは任意）': 'Connect from world (optional for normal worlds)',
        '（接続しない）': '(no connection)',
        '既存マップから複製（任意・通常ワールドのみ）': 'Clone from existing map (optional, normal worlds only)',
        '（空テンプレート）': '(empty template)',
        '実行後は「生成・版番」→ generate:treasures、「検証」→ すべて実行、で確認してください（ワープ座標は仮値のため要調整）。': 'Afterwards run "Generate & version" → generate:treasures and "Validate" → run all (warp coordinates are placeholders and need adjustment).',
        '外部AI依頼文書の作成': 'Create an External AI Request Document',
        '対象ゲーム（選択すると設定済みデータから下の入力欄を自動入力）': 'Target game (selecting auto-fills the fields below from existing data)',
        '作品要約シート（入力すると依頼文と一緒に整形されます）': 'Story summary sheet (formatted together with the request text)',
        '依頼文': 'Request text',
        '作品要約シート コピー': 'Copy summary sheet',
        '使い方': 'How to use',
        '📥 MD ファイルをダウンロード': '📥 Download as MD file',
        '新規ゲーム': 'New game',
        'ゲーム詳細へ': 'Open game detail',
        '✔ スキャフォールド完了': '✔ Scaffold complete',
        '✖ 失敗': '✖ Failed',
        '手動確認チェックリスト（手順 7）': 'Manual check list (step 7)',
        'プレビュー起動（F7）': 'Preview server (F7)',
        'i18n チェック（F5）': 'i18n check (F5)',
        'アセット存在チェック（F6）': 'Asset existence check (F6)',
        '公開準備（F9）': 'Publish preparation (F9)',
        // ── 追加分（2026-07-11 全件カバレッジ検査で補完）────────────
        // ジョブ種別・共通結果
        'スキャフォールド': 'Scaffold',
        '画像の自動生成': 'Automatic image generation',
        '3Dキャラの自動生成（Meshy）': 'Automatic 3D character generation (Meshy)',
        '取り込みと検証': 'Import & validation',
        '原因不明（画面下のログ欄を確認してください）': 'Unknown cause (check the log at the bottom of the screen)',
        '原因不明（画面下のログ欄と logs/ の監査ログを確認してください）': 'Unknown cause (check the on-screen log and the audit log)',
        'プレビューを開けませんでした。このサイトのポップアップを許可して再度実行してください。': 'Could not open the preview. Allow pop-ups for this site and try again.',
        '一覧の取得に失敗しました:': 'Failed to load the list:',
        'プロジェクトを取得できません:': 'Could not load the project:',
        '削除に失敗しました:': 'Delete failed:',
        '開始に失敗しました': 'Failed to start',
        '依頼文の生成に失敗しました': 'Failed to generate the request text',
        '取り込み後の検証で問題が見つかりました（ログ参照）。「⑥ JSON設計」タブでユニットの再生成や修正ができます。': 'Validation after import found issues (see log). You can regenerate or fix units in the "⑥ JSON design" tab.',
        '※この操作は取り消せません。': '* This cannot be undone.',
        '削除されるもの: プロジェクト一式とブラウザ内のベースデータ・プレビュー用作業データ': 'Deletes: the whole project and its in-browser base and preview working data',
        '削除されるもの: プロジェクト一式（解析結果・キャスト画像・画像プロンプト・ワールド設計・原文コピー）': 'Deletes: the whole project (analysis, cast images, image prompts, world design, source copy)',
        '残るもの: 生成済みのゲーム本体（newgames/）と 3D モデル（assets/newgames/）': 'Keeps: the generated game itself (newgames/) and 3D models (assets/newgames/)',
        // 要約シート・解析タブ
        '作品タイトル': 'Title',
        'プレイアブルキャラ': 'Playable characters',
        '都市・マップ一覧': 'Cities / maps',
        '主要建築物': 'Key buildings',
        'NPC 一覧': 'NPCs',
        '敵一覧': 'Enemies',
        'アイテム一覧': 'Items',
        'スキル一覧': 'Skills',
        '章イベント': 'Chapter events',
        'BGM / SE 方針': 'BGM / SE policy',
        '翻訳方針': 'Translation policy',
        '（未記入）': '(not filled)',
        '人型': 'Humanoid',
        '非人型': 'Non-humanoid',
        '（上のプレビューボタンから起動できます）': '(launch from the preview button above)',
        '（このプロジェクトから再生成できます）': '(can be regenerated from this project)',
        '先に「① 解析」を実行してください。': 'Run "① Analysis" first.',
        // ② ベースデータ生成タブ
        'gameId・作品名・2 キャラ構成・開始ワールド ID は解析結果（①のタブで編集可）が使われます。': 'The gameId, title, two-character setup, and starting world ID come from the analysis (editable in tab ①).',
        '後続工程の土台として、gameId・作品名・2キャラ設定・共有マスタ・開始ワールドのベースデータを作成します。完成したワールドや3Dキャラを作る工程ではありません。': 'Creates the gameId, title, two-character configuration, shared masters, and starting-world base data used by later steps. This step does not create the finished world or 3D characters.',
        '作成後は③以降でキャラ画像・3Dモデル・ワールド設計・ゲームJSONを生成します。': 'After this, steps ③ and later generate character images, 3D models, the world design, and game JSON.',
        '（後続工程のベースデータを作成済み）': '(base data for later steps is ready)',
        '複製後に npm ci を実行する（JSON 生成のスキーマ検証に必要）': 'Run npm ci after cloning (needed for schema validation during JSON generation)',
        '（モデル情報なし）': '(no model info)',
        // ③ プロンプト・画像・3D
        '画像プロンプト:': 'Image prompt:',
        '③ キャラ生成でキャスト画像を先に生成してください': 'Generate the cast image in ③ Character generation first',
        '「3Dキャラを自動生成」は Meshy API で 画像→3D→テクスチャ（→リギング→アニメ×3）→GLB 配置まで自動実行します（プレイアブル ≒33P〜・静的 ≒30P の Meshy クレジット消費・1体 数分〜30分。二重課金防止のため成功済み工程は再利用）。': '"Auto-generate 3D character" runs image → 3D → texture (→ rigging → 3 animations) → GLB placement automatically via the Meshy API (≈33P+ for playable, ≈30P for static; a few minutes to 30 min per character; completed stages are reused to avoid double billing).',
        '「3Dキャラを自動生成」は Meshy API で画像→3D→テクスチャ→GLB配置まで実行します。プレイアブルはリギング＋アニメ3本（≒39P）、人型敵はリギング＋アニメ2本（≒36P）、非人型敵/NPCは静的GLB（≒30P）です。成功済み工程は再利用します。': '"Auto-generate 3D character" runs image → 3D → texture → GLB placement through the Meshy API. Playable characters get rigging plus 3 animations (≈39P), humanoid enemies get rigging plus 2 animations (≈36P), and non-humanoid enemies/NPCs get static GLBs (≈30P). Completed stages are reused.',
        '画像→3D（lowpoly・Aポーズ）→テクスチャ→リギング→アニメ×3 →GLB 3点配置': 'image → 3D (lowpoly, A-pose) → texture → rigging → 3 animations → 3 GLB placements',
        '画像→3D（lowpoly）→テクスチャ →GLB 1点配置': 'image → 3D (lowpoly) → texture → 1 GLB placement',
        '≒33P〜（20+10+リグ+アニメ×3）': '≈33P+ (20+10+rig+3 anims)',
        '≒30P（20+10）': '≈30P (20+10)',
        // ⑥ 生成・取り込み
        '生成済み': 'Generated',
        '空・照明・天候（environment.json）': 'Sky, lighting & weather (environment.json)',
        '旧形式の設計です。物語に応じた実寸・自然・生活環境・生息地を反映するため、新仕様で再設計してください。': 'This design uses the old format. Redesign it with the new format to reflect story-scaled dimensions, nature, lived-in environments, and habitats.',
        '新仕様で再設計': 'Redesign with new format',
        'ベースデータ未生成のため表示できません（② ベースデータ生成の後に利用可能）。': 'Not available until base data is created (after ② Base data generation).',
        '取り込みが完了しました。プレビューで動作を確認できます。': 'Import complete. You can check it in the preview.',
        '取り込みに問題があります（ログ参照）。': 'Import has issues (see log).',
        'プレビューを開く': 'Open preview',
        '取り込み・検証完了': 'Import & validation complete',
        '取り込みまたは検証に問題があります（ログ参照）': 'Import or validation has issues (see log)',
        '要対応': 'needs attention',
        '不足あり': 'missing items',
        '取り込み後は「検証」「生成・版番」タブで validate / generate:treasures を実行してください。': 'After importing, run validate / generate:treasures from the "Validate" and "Generate & version" tabs.',
        // 検証・アセット・プレビュー・公開（ゲーム詳細）
        '取得に失敗': 'failed to load',
        '本番 CDN を確認しています…（アセット数によって時間がかかります）': 'Checking the production CDN… (may take a while depending on asset count)',
        'アセットを確認しています…': 'Checking assets…',
        '停止中': 'Stopped',
        'チェックの保存に失敗しました:': 'Failed to save the checklist:',
        '起動に失敗しました:': 'Failed to start:',
        '停止に失敗しました:': 'Failed to stop:',
        '生成系（F8）': 'Generators (F8)',
        'playerModelCache.version も更新（コアキャラ GLB 差し替え時）': 'Also bump playerModelCache.version (when core character GLBs are replaced)',
        '生成系実行': 'Generator run',
        '公開後の JSON 更新は、この版番更新をしないと既存クライアントに配信されません。': 'After publishing, JSON updates are not delivered to existing clients unless this version bump is done.',
        'ビルド': 'Build',
        '公開機能を利用できます。予定 URL:': 'Publishing is available. Planned URL:',
        '現在、このツールから本番公開はできません:': 'Publishing from this tool is not available yet:',
        'dist 生成:': 'dist build:',
        '実データ依存テスト': 'Data-dependent tests',
        // キャラ設定・ワールド追加・ウィザード
        '新しいキャラ ID（英小文字。公開後は変更禁止＝セーブ互換が壊れます）': 'New character ID (lowercase; must not change after publishing — breaks save compatibility)',
        'modelPathIdle（../assets/... 形式）': 'modelPathIdle (../assets/... format)',
        '無限宝物庫': 'Infinite vault',
        '帰還先ワールド（必須）': 'Return world (required)',
        // AI 依頼文
        '✔ 物語プロジェクト': '✔ Story project',
        'の解析データから自動入力しました（編集可能）': ' — auto-filled from its analysis data (editable)',
        'このゲームには物語プロジェクトの解析データが無いため、ゲームのメタ情報のみ自動入力しました（残りは手入力）': 'This game has no story-project analysis, so only its metadata was auto-filled (fill in the rest manually)',
        // API設定 ガイド（リンク・code で分割されるテキスト断片）
        'でアカウントを作成（Google アカウント等でサインアップ可）': 'to create an account (you can sign up with Google, etc.)',
        '② 右上メニュー →「Keys」（': '② Top-right menu → "Keys" (',
        '）→「Create Key」で新しいキーを作成': ') → create a new key with "Create Key"',
        '③ 「Credits」（': '③ Open "Credits" (',
        '）でクレジットをチャージ（$5〜。解析＋画像一式で ≒$0.1〜0.3/作品）': ') and top up credits ($5+; analysis + all images ≈ $0.1–0.3 per story)',
        'で始まるキーをコピーして上の欄へ貼り付け →「保存」→「接続テスト」': ', copy the key that starts with it into the field above → "Save" → "Connection test"',
        'でアカウントを作成': 'to create an account',
        '② 右上のアイコン →「Settings」→「API」（': '② Top-right icon → "Settings" → "API" (',
        '）→「Create API Key」で新しいキーを作成': ') → create a new key with "Create API Key"',
        '※ クレジット（P）残高は': '* Check your credit (P) balance at',
        'で確認。3D キャラ 1体 ≒30〜45P（無料枠あり・不足時は有料プラン）': '. One 3D character ≈ 30–45P (free tier available; paid plans if short)',
        'キーの削除に失敗しました:': 'Failed to delete the key:',
        // 認証
        // backend（ブラウザ版 API）
        'Meshy キーが未設定です。API設定画面で登録してください': 'Meshy key is not set. Register it in API Settings.',
        'service は openrouter か meshy を指定してください': 'service must be openrouter or meshy',
        'text（依頼文）が空です': 'text (request) is empty',
        '3D モデル自動生成（Meshy）': '3D model auto-generation (Meshy)',
        'キャスト画像の自動生成': 'automatic cast image generation',
        'Meshy 接続テスト': 'Meshy connection test',
        // lib 由来（アラートに出るエラー）
        'ベースデータが未生成です（② ベースデータ生成を先に実行）。': 'Base data has not been generated yet (run ② Base data generation first).',
        'ベースデータが未生成です。先に「② ベースデータ生成」を実行してください。': 'Base data has not been generated yet. Run "② Base data generation" first.',
        'ワールド設計がありません。先に「④ 初期ワールド設計」を実行してください。': 'No world design yet. Run "④ World design" first.',
        '生成結果（generated/import.txt）がありません。先に「⑥-2 JSON 生成」を実行してください。': 'No generated output (generated/import.txt). Run "⑥-2 JSON generation" first.',
        '取り込める JSON がありません。⑥-2 JSON 生成を再実行してください。': 'No JSON to import. Re-run ⑥-2 JSON generation.',
        '「API設定」画面で OpenRouter キーの登録と接続テストを確認してください。': 'Check your OpenRouter key and run the connection test in "API Settings".',
        'API キーと接続は有効です。モデル側の一時的な問題の可能性が高いため、時間をおいて「① 解析」を再実行してください。': 'Your API key and connection are fine. This is most likely a temporary model-side issue — wait a bit and re-run "① Analysis".',
        '省略記号（... / …）が含まれています。完全な JSON を貼り付けてください。': 'Contains ellipses (... / …). Paste the complete JSON.',
        '配置先に空の要素や . / .. は使用できません。': 'Destination paths must not contain empty segments or . / ..',
        '配置する JSON はオブジェクト形式である必要があります。': 'The JSON to place must be an object.',
        '保護ファイル（運用系 / 自動生成）のため取り込みを拒否しました。': 'Rejected: this is a protected (operational / auto-generated) file.',
        '既存のロケール JSON が破損しているためマージできません。先に手動で修復してください。': 'The existing locale JSON is corrupted and cannot be merged. Repair it manually first.',
        '既存ファイルの JSON が破損しています（この上書きで修復されます）': 'The existing JSON file is corrupted (this overwrite will repair it)',
        '既存ファイルへのマージには confirmOverwrite（--overwrite）が必要です': 'Merging into an existing file requires confirmOverwrite (--overwrite)',
        '既存ファイルの上書きには confirmOverwrite（--overwrite）が必要です': 'Overwriting an existing file requires confirmOverwrite (--overwrite)',
        '他のエントリに問題があるため、一括取り込みを適用しませんでした': 'Not applied because another entry has an error (all-or-nothing import)',
        'OpenRouter API キーが未設定です。ツールの「API設定」画面から、ご自身で発行したキー（https://openrouter.ai/settings/keys）を登録してください（キーはお使いのブラウザにのみ保存されます）。': 'No OpenRouter API key is set. Register your own key (https://openrouter.ai/settings/keys) in the tool\'s "API Settings" screen (the key is stored only in your browser).',
        'OpenRouter の API キーが無効です（HTTP 401）。「API設定」画面でご自身のキーを登録し直してください。': 'Your OpenRouter API key is invalid (HTTP 401). Re-register your key in "API Settings".',
        'プロンプトが提供元の規制（モデレーション）に接触した可能性があります（HTTP 403）。': 'The prompt may have hit the provider\'s moderation rules (HTTP 403).',
        '画像生成なら「grok へ切替」または「プロンプト自動修正」を選んで再実行してください。': 'For image generation, retry with "switch to grok" or "auto-sanitize prompt".',
        'OpenRouter のクレジットが不足しています（HTTP 402）。https://openrouter.ai/credits で残高を追加してください。': 'Not enough OpenRouter credits (HTTP 402). Top up at https://openrouter.ai/credits.',
        '応答の受信に失敗しました（通信が途中で切断された可能性があります。課金は発生している場合があります）。時間を置いて再実行してください。': 'Failed to receive the response (the connection may have dropped mid-transfer; you may still have been billed). Try again later.',
        'モデルが生成を拒否しました（content_filter）': 'The model refused to generate (content_filter)',
        '出力トークン上限に達しましたが本文が空でした（推論系モデルが思考でトークンを使い切った可能性）': 'Hit the output token limit with empty content (a reasoning model may have spent all tokens thinking)',
        'モデルの応答が空でした': 'The model returned an empty response',
        'プロンプトの内容（規制接触）の可能性があります。': 'The prompt may have hit content rules.',
        'モデル側の一時的な問題の可能性があります。': 'This may be a temporary model-side issue.',
        'LLM 呼び出しに失敗しました': 'LLM call failed',
        '画像生成に失敗しました': 'Image generation failed',
        '画像生成がタイムアウトしました。サーバ側で生成（課金）は継続している可能性があります。時間を置いて再実行してください。': 'Image generation timed out. Generation (and billing) may still be running server-side. Try again later.',
        '画像プロンプトが未生成です。先に「③-1 画像生成プロンプト」を実行してください。': 'No image prompts yet. Run "③-1 Image-generation prompts" first.',
        'LLM 応答が空です': 'Empty LLM response',
        'プロンプト再生成': 'Regenerate prompt',
        '画像再生成': 'Regenerate image',
        'GLB再生成': 'Regenerate GLBs',
        'ESC でいつでも中断できます。実行しますか？': 'You can cancel anytime with ESC. Proceed?',
        'GLB: プレイアブル ≒33P〜・敵/NPC ≒30P × 体数（Meshy クレジット / 1体あたり数分〜30分）': 'GLBs: playable ≈33P+, enemy/NPC ≈30P per cast (Meshy credits / minutes to ~30 min each)',
        'GLB: プレイアブル ≒39P〜・人型敵 ≒36P〜・非人型敵/NPC ≒30P × 体数（Meshy クレジット / 1体あたり数分〜30分）': 'GLBs: playable ≈39P+, humanoid enemy ≈36P+, non-humanoid enemy/NPC ≈30P per cast (Meshy credits / minutes to ~30 min each)',
        '（Meshy クレジット消費: プレイアブル ≒33P〜・静的 ≒30P / 所要: 数分〜30分）': '(Meshy credits: playable ≈33P+, static ≈30P / time: minutes to ~30 min)',
        'クリックで 3D ビューア表示（生成済みの場合）': 'Click to open in the 3D viewer (if generated)',
        '表示する GLB が指定されていません。': 'No GLB specified to display.',
        'ダウンロード': 'Download',
        '表示中のGLBファイルをローカルPCへ保存します': 'Save the displayed GLB file to your computer',
        // 開始ワールドの土台（マイワールド）
        '基本（平原）': 'Basic (plains)',
        '✖ 未配置': '✖ Missing',
        '画像': 'Image',
        // 設定画面イントロ（<b> で分割されるテキスト断片）
        '本ツールの LLM 解析・JSON 生成・画像生成は': 'This tool calls',
        '、3D キャラ生成は': 'for LLM analysis / JSON / image generation, and',
        'の API を、': 'for 3D character generation — directly, using an',
        'で直接呼び出します（実費はそれぞれのアカウントに課金。提供元が用意したデフォルトキーはありません）。': 'API key you issue yourself (costs are billed to your own accounts; no default keys are provided).',
        '利用者自身が発行したキー': 'API key you issue yourself',

        // ── ボタンツールチップ（BUTTON_TIPS 全件）──────────────
        '小説などのテキストを登録し、解析→ゲーム生成→画像→3D→JSON 取り込みまで自動で進める新規プロジェクトを作成します': 'Create a new project from novel text and advance automatically through analysis → game generation → images → 3D → JSON import',
        'ゲーム ID やタイトルを自分で入力して、空のゲーム環境（スキャフォールド）を作成します': 'Create an empty game environment (scaffold) by entering the game ID and title yourself',
        'このゲーム環境（newgames/ 配下のフォルダ一式）を削除します。実行前に確認ダイアログが表示されます': 'Deletes this game environment (its entire folder). A confirmation dialog is shown first',
        '入力した物語テキストでプロジェクトを作成し、① 解析画面へ進みます': 'Creates a project from the entered story text and moves to the ① Analysis screen',
        '物語テキストを LLM で解析し、ゲーム企画（タイトル・キャスト・ワールド案）を抽出します（OpenRouter に実費が発生）': 'Analyzes the story text with an LLM and extracts the game plan (title, cast, world ideas) — OpenRouter cost applies',
        '物語テキストを再度 LLM 解析し、現在の解析結果（analysis.json）を上書きします（OpenRouter に実費が発生）': 'Re-analyzes the story text and overwrites the current analysis (analysis.json) — OpenRouter cost applies',
        '画面上で編集した解析結果（タイトル・キャスト等）を analysis.json に保存します': 'Saves the analysis (title, cast, etc.) edited on screen to analysis.json',
        'ゲーム生成で作られるファイル一覧を実行せずに確認します（ファイルは変更されません）': 'Previews the files game generation would create, without running it (no files are changed)',
        'スキャフォールドを実行し、newgames/ 配下にゲーム環境一式を生成します': 'Runs the scaffold and generates the full game environment',
        '実行内容（作成されるファイル一覧）を事前確認します（ファイルは変更されません）': 'Previews what will be created (no files are changed)',
        '実行内容を事前確認します（ファイルは変更されません）': 'Previews the run (no files are changed)',
        '全キャストの画像生成用プロンプト（A字型/シーンポーズ）を LLM で作成します（OpenRouter に実費が発生）': 'Creates image prompts (A-pose / scene pose) for all casts with an LLM — OpenRouter cost applies',
        '全キャストの画像プロンプトを作り直します（既存のプロンプトは上書き・OpenRouter に実費が発生）': 'Rebuilds image prompts for all casts (overwrites existing ones) — OpenRouter cost applies',
        'この英語プロンプトをクリップボードへコピーします（外部の画像生成 AI に貼り付けて使えます）': 'Copies this English prompt to the clipboard (paste it into an external image AI)',
        'このキャストの画像を OpenRouter（FLUX）で自動生成します（1枚 ≒$0.014 の実費が発生）': 'Auto-generates this cast\'s image via OpenRouter (FLUX) — ≈$0.014 per image',
        '全キャスト分の画像をまとめて再生成します（生成済みの画像も作り直し・枚数 × ≒$0.014 の実費が発生）': 'Regenerates images for every cast (existing images are redone; ≈$0.014 × count)',
        '全キャスト分の GLB 一式を Meshy でまとめて再生成します（画像を変更したキャストのみ実費・変更なしはキャッシュ再利用で追加消費 0P）': 'Regenerates the full GLB sets for every cast with Meshy (only casts with changed images incur cost; unchanged ones reuse the cache at no extra cost)',
        '未完了の工程（① 解析〜⑥ 取り込み）を順番に自動実行します。開始前に工程一覧と実費の目安を確認でき、ESC キーで中断できます': 'Runs the remaining steps (① Analysis – ⑥ Import) automatically in order. You can review the steps and estimated cost first, and cancel with ESC',
        'ブラウザ内の作業データでゲームを起動します（新しいタブ）': 'Launches the game from the in-browser working data (new tab)',
        'ステップ表示を最新の状態に更新します': 'Refreshes the step display',
        'ページを再読み込みします': 'Reloads the page',
        '手元の画像ファイルをアップロードして、このキャストの画像を差し替えます': 'Uploads a local image file to replace this cast\'s image',
        'この対象を LLM でもう一度生成し直します（実費が発生）': 'Regenerates this item with the LLM (cost applies)',
        'キャスト画像から Meshy API で 3D モデル（GLB）を自動生成して推奨配置先へ保存します（Meshy クレジット消費・数分〜30分）': 'Auto-generates a 3D model (GLB) from the cast image via the Meshy API and saves it to the recommended path (uses Meshy credits; minutes to ~30 min)',
        'この GLB モデルを 3D ビューアで表示します（ドラッグで回転・ホイールで拡大縮小）': 'Opens this GLB in the 3D viewer (drag to rotate, scroll to zoom)',
        '解析結果と全テンプレート・ブロック・地形・天候仕様から、物語の縮尺・自然・生活環境・生息地を LLM で設計します（OpenRouter に実費が発生）': 'Uses the analysis plus all template, block, terrain, and weather capabilities to design story-scaled nature, lived-in environments, and habitats with an LLM — OpenRouter cost applies',
        '④ 初期ワールド設計（物語の縮尺・地形・自然・生活環境・生息地を設計）': '④ Initial world design (story scale, terrain, nature, lived-in environments, and habitats)',
        'ワールド設計をやり直して上書きします（OpenRouter に実費が発生）': 'Redoes and overwrites the world design — OpenRouter cost applies',
        '画面上で編集したワールド設計を保存します': 'Saves the world design edited on screen',
        'ゲームデータ作成を AI に依頼するための依頼文（Markdown）を生成します': 'Generates the request text (Markdown) for asking an AI to create game data',
        '依頼文を作り直します（画面での編集内容は破棄されます）': 'Rebuilds the request text (on-screen edits are discarded)',
        '画面上で編集した内容を保存します': 'Saves your on-screen edits',
        'チェックしたユニット（マップ・NPC 等の JSON）を LLM で生成します（OpenRouter に実費が発生）': 'Generates the checked units (map, NPC, etc. JSON) with an LLM — OpenRouter cost applies',
        '生成済み JSON をゲームへ取り込む際の差分を実行せずに確認します': 'Previews the diff of importing the generated JSON, without applying it',
        '生成済み JSON をゲームへコピーし、スキーマ・整合・翻訳・アセットの検証まで実行します': 'Copies the generated JSON into the game and runs schema / integrity / i18n / asset validation',
        '生成された内容をゲームデータへ配置します': 'Places the generated content into the game data',
        '4種類の検証（JSON スキーマ・データ整合・翻訳の欠落・アセット実在）をまとめて実行します': 'Runs all four validations (JSON schema, data integrity, missing translations, asset existence)',
        'マップ JSON のスキーマ検証を実行します': 'Runs schema validation on the map JSON',
        'ID 参照や座標などのデータ整合チェックを実行します': 'Runs integrity checks (ID references, coordinates, etc.)',
        '宝箱データの自動生成スクリプトを実行します': 'Runs the treasure data generation script',
        'ブラウザキャッシュを更新させるためにデータキャッシュのバージョン番号を上げます（maps JSON 変更後に必須）': 'Bumps the data-cache version so browsers refresh their cache (required after changing maps JSON)',
        'ゲームの依存パッケージをクリーンインストールします（初回起動前に必要・数分かかります）': 'Clean-installs the game\'s dependencies (needed before first launch; takes minutes)',
        'ゲームの本番用ビルド（vite build）を実行します': 'Runs the production build (vite build)',
        'このゲームのローカル開発サーバを起動します（ブラウザで動作確認できます）': 'Starts this game\'s local dev server (check it in the browser)',
        'ローカル開発サーバを停止します': 'Stops the local dev server',
        'ゲームが参照するアセット（GLB・画像）がローカルに存在するか確認します': 'Checks that the assets the game references (GLB, images) exist locally',
        '本番 CDN（R2）にアセットが配信済みか HEAD リクエストで確認します': 'Checks via HEAD requests that assets are on the production CDN (R2)',
        '現行の共有ランタイム方式で公開可能か、必要な検証と未完了項目を確認します': 'Checks whether the game can be published with the shared-runtime scheme, and what is still missing',
        'このゲームの管理画面（検証・生成・起動・3D モデル）を開きます': 'Opens this game\'s management screens (validate, generate, launch, 3D models)',
        'この対象のプロンプト全文を表示します': 'Shows the full prompt for this item',
        'この内容をクリップボードへコピーします': 'Copies this to the clipboard',
        '入力したキーをこのブラウザの localStorage に保存します': 'Saves the entered key in this browser\'s localStorage',
        '保存済みのキーで実際に API へ接続し、残高や疎通状態を確認します': 'Connects to the API with the saved key to verify balance and reachability',
        '依頼文と作品要約シートを1つのMarkdownファイルとして保存します（ChatGPT / Gemini アプリへそのままアップロードできます）': 'Saves the request text and summary sheet as one Markdown file (upload it directly to ChatGPT / Gemini apps)',
        'この処理を実行します': 'Runs this operation',
        'アカウント設定（プロフィール・パスワード・ログアウト）を開きます': 'Opens account settings (profile, password, sign out)',
        'ログイン画面を開きます': 'Opens the sign-in screen',
        '確認のうえ、登録メールアドレスへパスワード再設定用のリンクを送ります': 'After confirmation, sends a password reset link to your registered email',
        '確認のうえ、このブラウザからログアウトします': 'After confirmation, signs out of this browser',
        'パスワードの表示/非表示を切り替えます（入力ミスの目視確認用）': 'Toggle password visibility (to check for typos)',
        '作成済みゲームと物語プロジェクトの一覧（ホーム画面）を表示します': 'Shows your games and story projects (Home)',
        '作成済みの物語プロジェクト一覧（ホーム画面）を表示します': 'Shows your story projects (Home)',
        'AI にゲームデータ作成を依頼するための依頼文（プロンプト）を生成する画面を開きます': 'Opens the screen for generating an AI request prompt for game data',
        'OpenRouter の API キーを登録・接続テストする画面を開きます': 'Opens the screen for registering and testing your OpenRouter API key',
        'OpenRouter / Meshy の API キーを登録・接続テストする画面を開きます': 'Opens the screen for registering and testing your OpenRouter and Meshy API keys',
        'ブラウザ内の作業データでゲームを起動して動作確認します（/preview/ を新しいタブで開きます）': 'Launches the game from in-browser data for checking (opens /preview/ in a new tab)',
        '現在の進捗で次に必要な画面を開きます': 'Opens the next screen needed at your current progress',
        '公開中のゲームの公開状態を確認します': 'Opens the publish settings of your published game',
        '①〜⑥が完了しました。URLを知っている人だけが遊べる形で公開できます': 'Steps ①–⑥ are complete. You can publish it so only people with the URL can play',
        '① 解析画面を開いて、物語の原文ファイルを登録し直します': 'Opens ① Analysis so you can register the story file again',
        'メールアドレスで新規アカウントを作成します': 'Creates a new account with your email address',
        'ログイン画面へ戻ります': 'Returns to the sign-in screen',
        'Google アカウントでログインします': 'Sign in with your Google account',
        'Apple アカウントでログインします': 'Sign in with your Apple account',
        'このブラウザに保存されているキーを削除します': 'Deletes the key stored in this browser',
        'GLB変更': 'Replace GLBs',
        'GLBファイル変更': 'Replace GLB files',
        '閉じる': 'Close',
        'キャラが使用するGLBを種類ごとに個別変更できます。指定した種類のファイルだけが上書きされます。': 'Replace each GLB type used by the character independently. Only the selected type is overwritten.',
        'このGLBを変更': 'Replace this GLB',
        '待機・移動・バトル等の GLB ファイルを種類ごとに個別変更します': 'Replace idle, movement, battle, and other GLB files independently by type.',
        'この種類に選択した GLB ファイルを上書き保存します': 'Overwrite this type with the selected GLB file.',
        'GLBファイルを選択してください': 'Select a GLB file.',
        'GLB形式のファイルを指定してください': 'Select a file in GLB format.',
        'GLBが大きすぎます（上限 25MB）': 'The GLB is too large (25 MB maximum).',
        'GLBをアップロードしています…': 'Uploading the GLB…',
        'GLBの変更に失敗しました': 'Failed to replace the GLB.',
        '待機（_wait）': 'Idle (_wait)',
        '移動（_move）': 'Movement (_move)',
        'バトル（_btl）': 'Battle (_btl)',
        '統合GLB（idle/walk）': 'Unified GLB (idle/walk)',
        '静的モデル': 'Static model',
        'プレイアブルと人型敵はリギング向けの A 字型、人型 NPC はシーンに合う自然な姿、非人型は自然体の英語画像プロンプトを OpenRouter で生成します。': 'OpenRouter generates English image prompts: A-pose for playable characters and humanoid enemies, natural scene poses for humanoid NPCs, and natural poses for non-humanoids.',

        // ── 2026-07-13 全画面 i18n 監査で補完 ──────────────
        'API キー:': 'API key:',
        'API設定画面でキーの保存と接続テストを行います': 'Save your key and run a connection test on the API Settings screen',
        'ここにゲームシナリオ文書……（200文字以上）': 'Paste the game scenario document here… (200+ characters)',
        '（8文字以上）': '(8+ characters)',
        'サンプルゲームとして作成（data/ のサンプルクエスト一式を適用。初回チュートリアル向け）': 'Create as a sample game (applies the complete sample quest from data/; suitable for the first tutorial)',
        'サンプルクエストを適用する': 'Apply the sample quest',
        'gameId（英小文字・数字・_-。例: clockwork_city）': 'gameId (lowercase letters, numbers, _ and -; e.g. clockwork_city)',
        'ユニーク化接尾辞（_xxxx）を自動付与（推奨: 公開サーバ全体で ID が衝突しないようにする）': 'Automatically add a unique suffix (_xxxx) (recommended to avoid ID collisions on the public server)',
        'スタートワールド ID': 'Starting world ID',
        '作品名（日本語）': 'Title (Japanese)',
        '作品名（英語）': 'Title (English)',
        '説明文（日本語）': 'Description (Japanese)',
        '説明文（英語）': 'Description (English)',
        'スタートキャラ（既定: 少年・少女の共通モデル 2 キャラ構成）': 'Starting characters (default: two shared-model characters, boy and girl)',
        '少年側': 'Boy character',
        '少女側': 'Girl character',
        '少年側 ID / 表示名(ja) / 表示名(en)': 'Boy ID / display name (ja) / display name (en)',
        '少女側 ID / 表示名(ja) / 表示名(en)': 'Girl ID / display name (ja) / display name (en)',
        '複製後に npm ci を実行する（時間がかかります）': 'Run npm ci after copying (this may take a while)',
        'sevenfold 実データ依存テストを削除せず保留する（既定は削除）': 'Keep sevenfold tests that depend on real data instead of removing them (removed by default)',
        '① ドライラン（実行内容の確認）': '① Dry run (review changes)',
        '公開サーバは全ゲーム共通のため、ID の重複を避けるランダム接尾辞（_xxxx）を自動で付けます': 'The public server is shared by all games, so a random suffix (_xxxx) is added to prevent duplicate IDs',
        '少年': 'Boy',
        '少女': 'Girl',

        '① 物語解析（要約シート・キャスト・企画）': '① Story analysis (summary, cast, and plan)',
        '④ 初期ワールド設計': '④ Initial world design',
        '⑥-1 AI 依頼文の生成・編集': '⑥-1 Generate and edit AI request',
        '⑥-2 JSON 生成（LLM 実行）': '⑥-2 JSON generation (LLM)',
        '⑥-3 取り込み・検証': '⑥-3 Import and validation',
        '未生成です（先に ① 解析が必要）。': 'Not generated yet (run ① Analysis first).',
        'マップ基本情報': 'Map information',
        '地形': 'Terrain',
        '空・照明・天候': 'Sky, lighting, and weather',
        '建物・地形オブジェクト': 'Buildings and terrain objects',
        'NPC 配置': 'NPC placement',
        '敵配置': 'Enemy placement',
        'イベント': 'Events',
        '（依頼文はまだ生成されていません。①解析 と ④ ワールド設計 の後に生成してください）': '(The request has not been generated yet. Generate it after ① Analysis and ④ World design.)',
        '最初の舞台': 'Starting setting',
        '舞台タイプ': 'Setting type',
        'プレイアブル': 'Playable characters',
        '区分': 'Role',
        '外見': 'Appearance',

        'AI 出力 JSON の取り込み（F2）': 'Import AI-generated JSON (F2)',
        '「ファイルパス + JSON コードブロック」形式の AI 出力を貼り付けてください。配置先は maps/ 配下のみ。運用系ファイルと treasures.json は拒否されます。': 'Paste AI output in “file path + JSON code block” format. Only destinations under maps/ are accepted; operational files and treasures.json are rejected.',
        'GLB パス': 'GLB path',
        '移動': 'Movement',
        '戦闘': 'Battle',
        'バトル': 'Battle',
        'NPC（マップ配置）': 'NPCs (map placement)',
        '敵（使用中の既存タイプ）': 'Enemies (existing types in use)',
        'ペット': 'Pets',
        'GLB プレビュー': 'GLB preview',
        'generate:treasures 実行': 'Run generate:treasures',
        'dataCache.version 更新': 'Update dataCache.version',
        '接続元ワールド（任意）': 'Source world (optional)',
        'ドライランで確認した内容のキャラ設定変更を実行します': 'Apply the character-setting changes reviewed in the dry run',
        'ドライランで確認した内容でワールドを追加します': 'Add the world using the changes reviewed in the dry run',
        'スタート地点でゲームが起動するか': 'The game starts at the intended starting point',
        'スタートメニューのタイトル・サブタイトルが作品に合っているか': 'The start-menu title and subtitle match the work',
        '主要マップ間ワープが成立するか（複数ワールド時）': 'Warping between major maps works (for multiple worlds)',
        'NPC 会話が意図した場所で出るか': 'NPC dialogue appears at the intended locations',
        '宝箱や敵が正しく出現するか': 'Treasure chests and enemies spawn correctly',
        'ショップ在庫が地域に応じて正しいか（hasShops=true 時）': 'Shop inventory matches the region (when hasShops=true)',
        '日本語 / 英語切替で主要文言が欠けていないか': 'Major text is complete when switching between Japanese and English',
        'BGM 切替や通知イベントが動くか': 'BGM changes and notification events work',
        '参照モデルや音源のパスが実在するか': 'Referenced model and audio paths exist',

        '前回結果:': 'Previous result:',
        '実行日時:': 'Run at:',
        '（現在は実行不要です）': '(No run is currently needed)',
        '前回結果: 中断しました（再実行待ち）': 'Previous result: canceled (waiting to re-run)',
        '完了しました。': 'Completed.',
        '（物語の登録後に実行できます）': '(Available after registering a story)',
        '準備完了': 'Ready',
        '（① 物語解析の完了後に実行できます）': '(Available after completing ① Story analysis)',
        '（⑥-1 AI依頼文の完了後に実行できます）': '(Available after completing ⑥-1 AI request)',
        '（⑥-2 JSON生成の完了後に実行できます）': '(Available after completing ⑥-2 JSON generation)',
        '要確認（再実行待ち）': 'Needs attention (waiting to re-run)',
        '要確認': 'Needs attention',
        'ファイル形式': 'File format',
        'データ': 'Data',
        '素材': 'Assets',
        '不明': 'Unknown',
        '生成ログを保存できませんでした': 'Could not save the generation log',
        'ページを再読み込みして一覧を取得し直します': 'Reload the page and retrieve the list again',
        'この物語プロジェクト一式（解析・画像・プロンプト・原文コピー）を削除します。実行前に確認ダイアログが表示されます': 'Deletes this entire story project (analysis, images, prompts, and source copy). A confirmation dialog is shown first',
        '対象キャストはすべて画像生成済みのためスキップしました。': 'Skipped because all target casts already have images.',
        '対象キャストはすべてGLB生成・反映済みのためスキップしました。': 'Skipped because all target casts already have generated and applied GLBs.',
        '実行中': 'Running',
        '一括実行全体': 'Entire batch run',
        '生成処理': 'Generation task',
        '（3D の自動生成は含まれません）': '(Automatic 3D generation is not included)',
        '一括実行ログの保存を開始できないため、処理を中止しました。': 'Stopped because the batch-run log could not be initialized.',
        '保存エラー': 'Save error',
        '✔ 一括実行が完了しました。': '✔ Batch run completed.',
        '■ 一括実行を中断しました。': '■ Batch run canceled.',
        '【一括実行の全体ログ】': '【Complete batch-run log】',
        '【個別実行した有料APIログ】': '【Individually run paid API logs】',

        '画像生成': 'Image generation',
        '3点（_wait / _move / _btl）': '3 files (_wait / _move / _btl)',
        '統合GLB 1点（idle/walk 2クリップ）': '1 unified GLB (idle/walk, 2 clips)',
        '1点': '1 file',
        'プレイアブル ≒39P': 'Playable ≈39P',
        '人型敵 ≒36P': 'Humanoid enemy ≈36P',
        '静的 ≒30P': 'Static ≈30P',
        'クリックで原寸表示': 'Click to view full size',
        '古い形式の設計です。再設計してください。': 'This design uses an old format. Redesign it.',
        'ワールド設計': 'World design',
        '依頼文を再生成（編集内容を破棄して作り直し）': 'Regenerate request (discard edits and rebuild)',
        'JSONを読み込めませんでした。': 'Could not load the JSON.',
        'JSONの最上位はオブジェクトにしてください。': 'The top level of the JSON must be an object.',
        '保存できませんでした。': 'Could not save.',
        '更新': 'Update',
        '動作': 'Action',
        '事前確認に失敗': 'Preflight failed',
        '事前確認に問題はありません。取り込みと検証を実行しますか？': 'Preflight passed. Run import and validation?',
        '失敗（再実行待ち）': 'Failed (waiting to re-run)',
        '3Dキャラ自動生成': 'Automatic 3D character generation',
        'エラー:': 'Errors:',
        '警告:': 'Warnings:',
        'スキップ': 'Skip',
        '配置先': 'Destination',
        '差分（トップキー）': 'Diff (top-level keys)',
        'ルール': 'Rule',
        'JSON パス': 'JSON path',
        '検証を開始できません': 'Could not start validation',
        '要対応があります': 'Action is required',
        'マスタ由来キーの欠落': 'Missing master-derived keys',
        '生文字列の未翻訳（en.json exact 未登録）': 'Untranslated raw strings (missing from en.json exact)',
        '文言': 'Text',
        '不足アセット': 'Missing asset',
        '本番CDN': 'Production CDN',
        '参照元': 'Referenced by',
        '不足分の解消（GLB/音源の制作・選定・R2 アップロード）は手動作業です。': 'Resolving missing assets (creating/selecting GLBs or audio and uploading to R2) is a manual task.',
        '依存準備': 'Dependency setup',
        '宝箱生成': 'Treasure generation',
        '版番更新': 'Version update',
        'npm ci を開始できません': 'Could not start npm ci',
        'generate:treasures を開始できません': 'Could not start generate:treasures',
        '更新に失敗しました': 'Update failed',
        '公開ビルド': 'Production build',
        'ビルドを開始できません': 'Could not start the build',
        '✔ 済': '✔ Done',
        '✖ 未（先にビルド）': '✖ Not ready (build first)',
        '確認チェックリスト': 'Verification checklist',
        '作品要約シート': 'Story summary sheet',
        '添付すべき資料': 'Files to attach',
        '再設計を実行': 'Run redesign',
        '差分・スキーマの事前確認後、F2配置、i18nマージ、treasures生成、整合・翻訳・アセット検証まで連続実行します': 'After diff and schema preflight, continuously runs F2 placement, i18n merge, treasure generation, and integrity, translation, and asset validation',
        '解析（ドライラン）': 'Analysis (dry run)',
        '解析': 'Analysis',
        'ゲーム生成': 'Game generation',
        '配置を実行': 'Apply placement',
        'ドライラン': 'Dry run',
        'すべて実行': 'Run all',
        '本番 CDN も確認': 'Check production CDN too',
        '次の作業': 'Next step',
        'キャンセル ＝ 次の選択肢へ': 'Cancel = show the next option',
        'OK ＝ 自動修正して生成': 'OK = sanitize and generate',
        'キャンセル ＝ 修正せずそのまま生成（規制で拒否される可能性あり）': 'Cancel = generate unchanged (the request may be rejected by content restrictions)',
    };

    // ── ② パターン（埋め込み値つき文言）──────────────────────
    const PATTERNS = [
        // 動的値とラベルが同じテキストノードになる画面（アカウント・進捗・モデル一覧）
        [/^メールアドレス: (.+)$/, (m) => `Email: ${m[1]}`],
        [/^ログイン方法: (.+)$/, (m) => `Sign-in method: ${m[1] === 'メール' ? 'Email' : m[1]}`],
        [/^・(.+)$/, (m) => `• ${translateLine(m[1])}`],
        [/^⚠ アダルト系表現の可能性があるキャスト: (.+)$/, (m) => `⚠ Cast potentially containing adult content: ${m[1]}`],
        [/^ゲーム連邦へ戻りました（終了記録に失敗: (.+)）$/, (m) => `Returned to the Game Federation (failed to record the visit end: ${m[1]})`],
        [/^（該当語: (.+)）$/, (m) => `(Matched terms: ${m[1]})`],
        [/^選択中のモデル（(.+)）では規制により生成が拒否されることがあります。$/, (m) => `The selected model (${m[1]}) may reject generation because of content restrictions.`],
        [/^OK ＝ 該当キャストのみ規制が最も緩い (.+)（\$0\.05\/枚）へ切り替えて生成（推奨）$/, (m) => `OK = switch only the affected cast to ${m[1]} ($0.05/image), the least restrictive option, and generate (recommended)`],
        [/^プロンプトを自動修正（性的・扇情的な表現だけを穏当化。キャラの特徴・構図は維持）して (.+) のまま生成しますか？$/, (m) => `Sanitize only sexual or suggestive wording while preserving character traits and composition, then generate with ${m[1]}?`],
        [/^前回結果: (.+)$/, (m) => `Previous result: ${translateLine(m[1])}`],
        [/^実行日時: (.+)$/, (m) => `Run at: ${m[1]}`],
        [/^解析完了（キャスト (\d+)人）$/, (m) => `Analysis complete (${m[1]} cast member(s))`],
        [/^キャラ (\d+)人 \/ 画像 (\d+)\/(\d+) \/ 3Dモデル (\d+)\/(\d+)$/, (m) => `Cast ${m[1]} / Images ${m[2]}/${m[3]} / 3D models ${m[4]}/${m[5]}`],
        [/^設計完了（地域 (\d+) \/ 建物 (\d+)）$/, (m) => `Design complete (${m[1]} area(s) / ${m[2]} building(s))`],
        // ⑤ 開始画面設計
        [/^背景 (.+) \/ 立ち絵 (\d+)\/(\d+)(.*)$/, (m) => `Background ${translateLine(m[1])} / Portraits ${m[2]}/${m[3]}${m[4] ? translateLine(m[4]) : ''}`],
        [/^キャラ選択立ち絵（プレイアブル (\d+)\/(\d+)）$/, (m) => `Character selection portraits (playable ${m[1]}/${m[2]})`],
        [/^先に完了が必要な工程: (.+)$/, (m) => `Steps to complete first: ${m[1].split(' ／ ').map(translateLine).join(' / ')}`],
        [/^「(.+)」の立ち絵を生成します。$/, (m) => `Generates the portrait of "${m[1]}".`],
        [/^「(.+)」の立ち絵$/, (m) => `the portrait of "${m[1]}"`],
        [/^(.+)を「(.+)」に差し替えますか？$/, (m) => `Replace ${translateLine(m[1])} with "${m[2]}"?`],
        [/^縦クロップ位置（(-?\d+)%）$/, (m) => `Vertical crop position (${m[1]}%)`],
        [/^背景スケール（×([\d.]+)）$/, (m) => `Background scale (×${m[1]})`],
        [/^キャラ基準位置（X: (-?\d+)%）$/, (m) => `Portrait base position (X: ${m[1]}%)`],
        [/^横位置（(-?\d+)px）$/, (m) => `Offset X (${m[1]}px)`],
        [/^縦位置（(-?\d+)px）$/, (m) => `Offset Y (${m[1]}px)`],
        [/^大きさ（×([\d.]+)）$/, (m) => `Scale (×${m[1]})`],
        [/^「(.+)」の立ち絵の足元（脚の間の白残り・床影）を追加で透過します。$/, (m) => `Additionally clears the foot area (white gap between the legs and floor shadow) of the portrait of "${m[1]}".`],
        [/^作成済み（(.+)）$/, (m) => `created (${translateLine(m[1])})`],
        [/^JSON生成 (\d+)\/(\d+)(.*)$/, (m) => `JSON generation ${m[1]}/${m[2]}${translateLine(m[3])}`],
        [/^取り込み (.+)$/, (m) => `Import ${translateLine(m[1])}`],
        [/^（(\d+)ファイル）$/, (m) => `(${m[1]} files)`],
        [/^ファイル形式 (.+) \/ データ (.+) \/ 翻訳 (.+) \/ 素材 (.+)$/, (m) => `File format ${translateLine(m[1])} / Data ${translateLine(m[2])} / i18n ${translateLine(m[3])} / Assets ${translateLine(m[4])}`],
        [/^\[(.+)\] ジョブ失敗:$/, (m) => `[${translateLine(m[1])}] job failed:`],
        [/^「(.+)」を削除しました。(.*)$/, (m) => `Deleted “${m[1]}”.${translateLine(m[2])}`],
        [/^（残高 \$([\d.]+)）$/, (m) => `(balance $${m[1]})`],
        [/^小説: ([\d,]+)字（(日本語|英語)）$/, (m) => `Novel: ${m[1]} chars (${m[2] === '日本語' ? 'Japanese' : 'English'})`],
        [/^使用モデル: (.+)$/, (m) => `Model: ${m[1]}`],
        [/^入力上限を超えるため、後ろ側 ([\d,]+)字は削除して処理します$/, (m) => `Beyond the input limit: the last ${m[1]} chars are dropped before processing`],
        [/^解析コスト（予測）: 約 (.+)$/, (m) => `Estimated analysis cost: about ${m[1]}`],
        [/^解析コスト（予測）: 算出できませんでした（(.+)）$/, (m) => `Estimated analysis cost: unavailable (${m[1]})`],
        [/^内訳: 入力 ([\d,]+)トークン ≒(.+) \/ 出力 最大 ([\d,]+)トークン ≒(.+)$/,
            (m) => `Breakdown: input ${m[1]} tokens ≈${m[2]} / output up to ${m[3]} tokens ≈${m[4]}`],
        [/^内訳: 入力 ([\d,]+)トークン \/ 出力 最大 ([\d,]+)トークン$/,
            (m) => `Breakdown: input ${m[1]} tokens / output up to ${m[2]} tokens`],
        [/^読み込める文書の上限: 日本語 ([\d,]+)字 \/ 英語ほか ([\d,]+)字（超えた分は後ろ側が削除されます）$/,
            (m) => `Maximum document size: ${m[1]} chars for Japanese / ${m[2]} chars for English and others (anything beyond the limit is dropped from the end)`],
        [/^「(.+)」からゲームシナリオ文書を作成しますか？$/, (m) => `Create a game scenario document from “${m[1]}”?`],
        [/^ファイルを読み込めませんでした（(.+)）$/, (m) => `Could not read the file (${m[1]})`],
        [/^ゲームシナリオ文書の作成に失敗しました:$/, () => 'Failed to create the game scenario document:'],
        [/^(.+)字(.*)$/, (m) => `${translateLine(m[1])} chars${translateLine(m[2])}`],
        [/^一括実行を開始: (.+)$/, (m) => `Started batch run: ${m[1]}`],
        [/^✖ 予期しないエラー: (.+)$/, (m) => `✖ Unexpected error: ${m[1]}`],
        [/^✖ 一括実行が失敗しました: (.+)$/, (m) => `✖ Batch run failed: ${m[1]}`],
        [/^(.+)一括実行ログの最終保存に失敗: (.+)$/, (m) => `${m[1]}Failed to save the final batch-run log: ${m[2]}`],
        [/^(.+)（(\d+)件の履歴）$/, (m) => `${translateLine(m[1])} (${m[2]} ${m[2] === '1' ? 'run' : 'runs'})`],
        [/^(.+)を選択$/, (m) => `Select ${translateLine(m[1])}`],
        [/^(.+)を編集$/, (m) => `Edit ${translateLine(m[1])}`],
        [/^事前確認: (\d+)ファイル$/, (m) => `Preflight: ${m[1]} file(s)`],
        [/^対象: (\d+)ファイル（更新: (\d+)）$/, (m) => `Target: ${m[1]} file(s) (${m[2]} update(s))`],
        [/^参照 (\d+)件中$/, (m) => `${m[1]} references`],
        [/^未配置 (\d+)件$/, (m) => `${m[1]} missing`],
        [/^✔ あり（(\d+)KB）$/, (m) => `✔ Available (${m[1]} KB)`],
        [/^(.+)（配置 (\d+)）$/, (m) => `${m[1]} (${m[2]} placement(s))`],
        [/^プロジェクト (.+)$/, (m) => `Project ${m[1]}`],
        [/^モデル確認 (\d+)件 \/ 未配置 (\d+)件$/, (m) => `Model check: ${m[1]} / Missing: ${m[2]}`],
        [/^ゲームが見つかりません: (.+)$/, (m) => `Game not found: ${m[1]}`],
        [/^ヒント: (.+)$/, (m) => `Hint: ${m[1]}`],
        [/^データ (.+)（エラー (\d+) \/ 警告 (\d+)）$/, (m) => `Data ${translateLine(m[1])} (${m[2]} error(s) / ${m[3]} warning(s))`],
        [/^en\.json messages に無いキー（(\d+)）$/, (m) => `Keys missing from en.json messages (${m[1]})`],
        [/^ja\.json messages に無いキー（(\d+)）$/, (m) => `Keys missing from ja.json messages (${m[1]})`],
        [/^参照 (\d+)件中 不足 (\d+)件(.*)$/, (m) => `${m[2]} of ${m[1]} references missing${translateLine(m[3])}`],
        [/^(.+) ほか (\d+)箇所$/, (m) => `${m[1]} and ${m[2]} more location(s)`],
        [/^版番更新 完了（(\d+)件）$/, (m) => `Version update complete (${m[1]})`],
        [/^完了（dist\/(.+)\/）$/, (m) => `Complete (dist/${m[1]}/)`],
        [/^現在、このツールから本番公開はできません: (.+)$/, (m) => `Production publishing is not currently available from this tool: ${m[1]}`],
        [/^✔ 物語プロジェクト (.+) の解析データから自動入力しました（編集可能）$/, (m) => `✔ Filled automatically from story project ${m[1]} analysis (editable)`],
        [/^…(.+)（([\d,]+)文字）$/, (m) => `…${m[1]} (${m[2]} characters)`],
        [/^（(.+)）— 下の「接続テスト」を実行してください$/, (m) => `(${m[1]}) — run “Connection test” below`],
        [/^残高 \$([\d.]+) \/ 上限 \$([\d.]+)$/, (m) => `Balance $${m[1]} / limit $${m[2]}`],
        [/^(.+)（使用済み \$([\d.]+)）$/, (m) => `${translateLine(m[1])} (used $${m[2]})`],
        [/^「(.+)」の画像プロンプトを再生成しますか？（既存は上書き・LLM 実費が発生します）$/, (m) => `Regenerate the image prompt for "${m[1]}"? (Overwrites the existing one; LLM cost applies)`],
        [/^「(.+)」の画像を自動生成します（1枚 ≒ \$([\d.]+)〜([\d.]+)）。$/, (m) => `Auto-generate the image for "${m[1]}" (≈$${m[2]}–${m[3]} per image).`],
        [/^「(.+)」の画像を「(.+)」に差し替えますか？$/, (m) => `Replace the image of "${m[1]}" with "${m[2]}"?`],
        [/^物語文書を「(.+)」に差し替えますか？$/, (m) => `Replace the story document with "${m[1]}"?`],
        [/^物語文書を読み込んでいます…$/, () => 'Loading the story document…'],
        [/^↺ 後工程を初期化: (.+)$/, (m) => `↺ Reset downstream steps: ${m[1]}`],
        [/^（現在の画像は上書きされます）$/, () => '(The current image will be overwritten)'],
        [/^選択した (\d+)ユニットの JSON を生成しますか？$/, (m) => `Generate JSON for the ${m[1]} selected unit(s)?`],
        [/^（LLM 実費が発生します。生成済みユニットは上書きされます）$/, () => '(LLM cost applies. Already-generated units will be overwritten)'],
        [/^このゲームシナリオ文書で新規プロジェクトを作成しますか？$/, () => 'Create a new project from this game scenario document?'],
        [/^（作成後の一括実行は、工程一覧と実費の確認ダイアログを経てから開始されます）$/, () => '(The follow-up batch run starts only after you confirm the step list and estimated cost)'],
        [/^この内容でユーザー登録しますか？$/, () => 'Create an account with this information?'],
        [/^(.+) 宛に確認メールを送信します。$/, (m) => `A confirmation email will be sent to ${m[1]}.`],
        [/^パスワード再設定メールを送信しますか？$/, () => 'Send a password reset email?'],
        [/^送信先: (.+)$/, (m) => `To: ${m[1]}`],
        [/^対象: (.+)ファイル \/ (.+)MB（対象外 (\d+)件は同梱されません）$/, (m) => `Files: ${m[1]} / ${m[2]} MB (${m[3]} ineligible files are excluded)`],
        [/^対象: (.+)ファイル \/ (.+)MB$/, (m) => `Files: ${m[1]} / ${m[2]} MB`],
        [/^生成3Dモデル: (\d+)件を同梱$/, (m) => `Generated 3D models: ${m[1]} bundled`],
        [/^対象: (.+)$/, (m) => `Account: ${m[1]}`],
        // ⑦ 公開・マイ公開ゲーム
        [/^容量: (.+)MB \/ (.+)MB ・ ゲーム数: (\d+)\/(\d+)$/, (m) => `Storage: ${m[1]} MB / ${m[2]} MB · Games: ${m[3]}/${m[4]}`],
        [/^容量: (.+)MB$/, (m) => `Size: ${m[1]} MB`],
        [/^このゲームは公開済みです（(.+) \/ rev (\d+)）$/, (m) => `Already published (${m[1]} / rev ${m[2]})`],
        [/^公開URL: (.+)$/, (m) => `Public URL: ${m[1]}`],
        [/^になります$/, () => '.'],
        [/^rev (\d+) ・ (.+)MB$/, (m) => `rev ${m[1]} · ${m[2]} MB`],
        [/^利用状況（(.+) プラン）$/, (m) => `Usage (${m[1]} plan)`],
        // 管理者プランの「無制限」表示（利用状況の値セル）
        [/^([\d.,]+)MB \/ 無制限$/, (m) => `${m[1]}MB / unlimited`],
        [/^(\d+) \/ 無制限$/, (m) => `${m[1]} / unlimited`],
        [/^上限: 1ゲームあたり (.+)MB ・ 公開できるゲーム数 (\d+)件$/, (m) => `Limits: ${m[1]} MB per game · up to ${m[2]} published game(s)`],
        [/^「(.+)」の公開状態を「(.+)」へ変更しますか？$/, (m) => `Change the visibility of "${m[1]}" to "${m[2]}"?`],
        [/^「(.+)」のギャラリー掲載審査を申請しますか？$/, (m) => `Request a gallery listing review for "${m[1]}"?`],
        [/^「(.+)」の審査（ギャラリー掲載の申請）を取り下げますか？$/, (m) => `Withdraw the review request (gallery listing) for "${m[1]}"?`],
        [/^審査前の状態に戻り、公開範囲の変更・更新公開・削除が再びできるようになります。$/, () => 'It returns to unreviewed, and visibility changes, update publishing and deletion become available again.'],
        [/^「(.+)」の公開を取り下げますか？$/, (m) => `Withdraw the publication of "${m[1]}"?`],
        [/^⑦ 限定公開へ進む: (.+)$/, (m) => `⑦ Unlisted publish: ${m[1]}`],
        [/^✔ (.+)で公開中$/, (m) => `✔ Published (${m[1]})`],
        [/^あと (.+) で公開できます$/, (m) => `Add ${m[1]} to publish`],
        [/^申請中は公開範囲の変更・内容の差し替え・取り下げができなくなります（申請の取り下げは可能です）。$/,
            () => 'While under review you cannot change visibility, replace contents or withdraw the publication (you can withdraw the request itself).'],
        [/^公開URLは直ちに 404 になり、公開したファイルは削除されます。$/, () => 'The public URL immediately returns 404 and the published files are removed.'],
        [/^このブラウザ内の作成データは残るため、あとで同じ URL で公開し直せます（ギャラリー掲載の審査はやり直しになります）。$/, () => 'Your work data in this browser is kept, so you can publish again later at the same URL (the gallery review starts over).'],
        [/^このブラウザ内の作成データは残るため、あとで同じ URL で公開し直せます。$/, () => 'Your work data in this browser is kept, so you can publish again later at the same URL.'],
        // プロジェクトの保存・取り込み（埋め込み値つき）
        [/^ゲームID「(.+)」は別のプロジェクトが使用中のため、複製としてのみ取り込めます。$/,
            (m) => `Game ID “${m[1]}” is in use by another project, so it can only be imported as a duplicate.`],
        [/^「(.+)」の作業データ一式を 1つのファイル（\.zip）に保存します。$/,
            (m) => `Saves all of your work on “${m[1]}” into a single file (.zip).`],
        [/^「(.+)」をファイルに保存しました。$/, (m) => `Saved “${m[1]}” to a file.`],
        [/^ファイル名: (.+)$/, (m) => `File name: ${m[1]}`],
        [/^サイズ: (.+)MB$/, (m) => `Size: ${m[1]} MB`],
        [/^ブラウザのダウンロード先をご確認ください。$/, () => "Check your browser's download folder."],
        [/^保存に失敗しました:$/, () => 'Failed to save:'],
        [/^このファイルを復元して取り込みますか？$/, () => 'Restore and import this file?'],
        [/^ゲームID: (.+)$/, (m) => `Game ID: ${m[1]}`],
        [/^※ 同じプロジェクトの現在の内容は置き換わります。$/, () => '* The current contents of the same project will be replaced.'],
        [/^別ゲームとして複製しますか？$/, () => 'Duplicate as another game?'],
        [/^新しいゲームID: (.+)$/, (m) => `New game ID: ${m[1]}`],
        [/^元のデータはそのまま残ります。$/, () => 'The original data is kept as is.'],
        [/^取り込みが完了しました。$/, () => 'Import complete.'],
        [/^取り込んだファイル: (\d+)件$/, (m) => `Imported files: ${m[1]}`],
        [/^（(.+) から複製しました）$/, (m) => `(Duplicated from ${m[1]})`],
        [/^ファイルを読み込めませんでした（(.+)）$/, (m) => `Could not read the file (${m[1]})`],
        // 公開審査（管理者）
        [/^申請中（(\d+)件）$/, (m) => `Pending (${m[1]})`],
        [/^審査済み・その他（(\d+)件）$/, (m) => `Reviewed / others (${m[1]})`],
        [/^rev (\d+) ・ (.+)MB ・ (\d+)ファイル$/, (m) => `rev ${m[1]} · ${m[2]} MB · ${m[3]} file(s)`],
        [/^審査中: (\d+) ・ 承認済み: (\d+) ・ 未申請: (\d+) ・ 要修正: (\d+) ・ 運営停止中: (\d+)（公開ゲーム (\d+)件）$/,
            (m) => `Under review: ${m[1]} · Approved: ${m[2]} · Not requested: ${m[3]} · Changes requested: ${m[4]} · Suspended: ${m[5]} (${m[6]} published game(s))`],
        [/^ギャラリーを再生成しました（掲載 (\d+)件）。$/, (m) => `Gallery rebuilt (${m[1]} listed).`],
        [/^「(.+)」の審査状態を「(.+)」にしますか？$/, (m) => `Set the review status of "${m[1]}" to "${translateLine(m[2])}"?`],
        [/^一括実行が「(.+)」で失敗しました:$/, (m) => `Batch run failed at "${m[1]}":`],
        [/^「(.+)」で失敗しました:$/, (m) => `Failed at "${m[1]}":`],
        [/^🚀 一括実行（(\d+)工程）$/, (m) => `🚀 Batch run (${m[1]} steps)`],
        [/^(.+) を実行中…（完了までお待ちください）$/, (m) => `Running ${m[1]}… (please wait until it finishes)`],
        [/^接続を再試行中…（(\d+)回目）$/, (m) => `Reconnecting… (attempt ${m[1]})`],
        [/^中断しています…（安全な区切りで停止します）$/, () => 'Canceling… (stopping at the next safe point)'],
        [/^中断要求が失敗しました（(.+)）。ESC でもう一度中断できます$/, (m) => `Cancel request failed (${m[1]}). Press ESC to try again`],
        [/^物語 ([\d,]+)文字を (\d+) チャンクに分割して抽出します$/, (m) => `Extracting: ${m[1]} characters split into ${m[2]} chunk(s)`],
        [/^([\d,]+)字$/, (m) => `${m[1]} chars`],
        [/^([\d,]+)字（200字以上必要です）$/, (m) => `${m[1]} chars (200+ required)`],
        [/^キャスト（(\d+)体）$/, (m) => `Cast (${m[1]})`],
        [/^…ほか (\d+)件$/, (m) => `…and ${m[1]} more`],
        [/^残高 \$([\d.]+) \/ 上限 \$([\d.]+)（使用済み \$([\d.]+)）$/, (m) => `Balance $${m[1]} / limit $${m[2]} (used $${m[3]})`],
        [/^上限なしキー（使用済み \$([\d.]+)）$/, (m) => `Key without a limit (used $${m[1]})`],
        [/^残高 \$([\d.]+) \/ 上限 \$([\d.]+)$/, (m) => `Balance $${m[1]} / limit $${m[2]}`],
        [/^OpenRouter 接続 OK$/, () => 'OpenRouter connection OK'],
        [/^Meshy 接続 OK$/, () => 'Meshy connection OK'],
        [/^接続テスト失敗（(.+)）:$/, (m) => `Connection test failed (${m[1]}):`],
        [/^ドライラン結果（gameId: (.+)）$/, (m) => `Dry run result (gameId: ${m[1]})`],
        [/^稼働中（(.+)）$/, (m) => `Running (${m[1]})`],
        [/^プレビュー: (.+)$/, (m) => `Preview: ${m[1]}`],
        [/^(\d+)件中 不足 (\d+)件。$/, (m) => `${m[2]} of ${m[1]} missing.`],
        [/^(\d+) 件中/, (m) => `of ${m[1]} total`],
        [/^このサービスのキーは (\S+) で始まります。キーを確認してください。$/, (m) => `Keys for this service start with ${m[1]}. Please check the key.`],
        [/^（ブラウザのパスワード自動入力で別サービスのキーが入っている場合があります。入力欄を空にして貼り付け直してください）$/, () => "(Your browser's autofill may have inserted a key for a different service. Clear the field and paste again.)"],

        // ── 追加パターン（2026-07-11 全件カバレッジ検査で補完）──────
        [/^ベース: (.+)$/, (m) => `Base: ${({ '基本（平原）': 'Basic (plains)', '海': 'Sea', '空': 'Sky', '砂': 'Sand', '宇宙': 'Space', '水中': 'Underwater' })[m[1]] ?? m[1]}`],
        [/^maps\/(.+)\/: placeholder（(.+) 複製）を生成$/, (m) => `maps/${m[1]}/: create placeholder (cloned from ${m[2]})`],
        [/^LLM 実費の目安: 解析〜JSON ≒\$0\.02〜0\.1(＋キャスト画像 ≒\$0\.01〜0\.05\/枚 × キャスト数)?(＋開始画面素材（背景1枚＋立ち絵×プレイアブル数）≒\$0\.01〜0\.05\/枚)?(（3D の自動生成は含まれません）)?$/, (m) => `Estimated LLM cost: analysis–JSON ≈$0.02–0.1${m[1] ? ' + cast images ≈$0.01–0.05 each × cast count' : ''}${m[2] ? ' + start screen art (1 background + portraits × playable count) ≈$0.01–0.05 each' : ''}${m[3] ? ' (automatic 3D generation not included)' : ''}`],
        [/^次の作業: (.+)$/, (m) => `Next step: ${translateLine(m[1])}`],
        [/^この GLB はまだ生成・配置されていません: (.+)$/, (m) => `This GLB has not been generated or placed yet: ${m[1]}`],
        [/^読み込みに失敗しました: (.+)（この GLB はまだ生成・配置されていない可能性があります）$/, (m) => `Failed to load: ${m[1]} (this GLB may not be generated or placed yet)`],
        [/^(.+) \/ 全高 ([\d.]+) \/ アニメ (\d+)本（ドラッグで回転・ホイールで拡大）$/, (m) => `${m[1]} / height ${m[2]} / ${m[3]} animation(s) (drag to rotate, scroll to zoom)`],
        [/^全 (\d+)キャストの GLB 一式を Meshy で再生成します。$/, (m) => `Regenerate the full GLB sets for all ${m[1]} casts with Meshy.`],
        [/^（画像未生成の (\d+)体はスキップします）$/, (m) => `(Skipping ${m[1]} cast(s) without images)`],
        [/^「(.+)」の GLB 一式（(.+)）を Meshy で再生成しますか？$/, (m) => `Regenerate the full GLB set (${m[2]}) for "${m[1]}" with Meshy?`],
        [/^先に「画像再生成」でキャスト画像を作成してください: (.+)$/, (m) => `Generate the cast image first with "Regenerate image": ${m[1]}`],
        [/^画像再生成（(.+)）$/, (m) => `Image regeneration (${m[1]})`],
        [/^GLB再生成（(.+)）$/, (m) => `GLB regeneration (${m[1]})`],
        [/^(.+)に失敗しました:$/, (m) => `${translateLine(m[1])} failed:`],
        [/^物語プロジェクト「(.+)（(.+)）」を削除しますか？$/, (m) => `Delete the story project "${m[1]} (${m[2]})"?`],
        [/^「(.+)（(.+)）」を削除しますか？$/, (m) => `Delete "${m[1]} (${m[2]})"?`],
        [/^「(.+)」を削除しました。$/, (m) => `Deleted "${m[1]}".`],
        [/^（ゲーム (.+) とのリンクを解除しました。ゲーム本体は残っています）$/, (m) => `(Unlinked game ${m[1]}; the game itself is kept)`],
        [/^（物語プロジェクト (.+) とのリンクを解除しました）$/, (m) => `(Unlinked story project(s): ${m[1]})`],
        [/^削除されるもの: ゲーム環境一式（newgames\/(.+)\/）$/, (m) => `Deletes: the whole game environment (newgames/${m[1]}/)`],
        [/^残るもの: 生成済みの 3D モデル等（assets\/newgames\/(.+)\/。不要なら手動で削除してください）$/, (m) => `Keeps: generated 3D models etc. (assets/newgames/${m[1]}/ — delete manually if unneeded)`],
        [/^不明な工程: (.+)$/, (m) => `Unknown step: ${m[1]}`],
        [/^JSON 構文エラー: (.+)$/, (m) => `JSON syntax error: ${m[1]}`],
        [/^キャスト（(\d+)体）$/, (m) => `Cast (${m[1]})`],
        [/^全 (\d+)キャストの画像を自動生成します。$/, (m) => `Auto-generate images for all ${m[1]} casts.`],
        [/^目安: 1枚 ≒ \$([\d.]+)〜([\d.]+) × (\d+)枚。$/, (m) => `Estimate: ≈$${m[1]}–${m[2]} per image × ${m[3]}.`],
        [/^「(.+)」の3Dキャラを Meshy で自動生成します。$/, (m) => `Auto-generate a 3D character for "${m[1]}" with Meshy.`],
        [/^工程: (.+)$/, (m) => `Steps: ${translateLine(m[1])}`],
        [/^消費: (.+)（Meshy クレジット）\/所要: 数分〜30分$/, (m) => `Cost: ${translateLine(m[1])} (Meshy credits) / Time: minutes to ~30 min`],
        [/^⚠ 対応するパス行が見つからないコードブロック (\d+)件をスキップしました（パス行の直後にコードブロックを置いてください）。$/, (m) => `⚠ Skipped ${m[1]} code block(s) without a matching path line (place each block right after its path line).`],
        [/^(\d+)件検出$/, (m) => `${m[1]} found`],
        [/^ほか (\d+)箇所$/, (m) => `and ${m[1]} more`],
        [/^「(.+)」は現在準備中のため、まだ利用できません。$/, (m) => `"${translateLine(m[1])}" is not available yet (coming soon).`],
        [/^ゲーム操作 (.+)$/, (m) => `game operation "${m[1]}"`],
        [/^不明な API: (.+)$/, (m) => `Unknown API: ${m[1]}`],
        [/^キーが無効です \(HTTP (\d+)\):(.*)$/, (m) => `The key is invalid (HTTP ${m[1]}):${m[2]}`],
        [/^（使用済み \$([\d.]+)）$/, (m) => `(used $${m[1]})`],
        [/^取り込み前検証で (\d+)ファイルに問題が見つかりました。i18n を含め、どのファイルも変更していません。$/, (m) => `Pre-import validation found problems in ${m[1]} file(s). Nothing was changed, including i18n.`],
        [/^全チャンクの抽出に失敗しました（原因: (.+)）。(.*)$/, (m) => `Extraction failed for all chunks (cause: ${translateLine(m[1])}). ${translateLine(m[2])}`],
        [/^JSON としてパースできません: (.+)（コメント・末尾カンマ・省略の可能性）$/, (m) => `Cannot parse as JSON: ${m[1]} (possibly comments, trailing commas, or omissions)`],
        [/^同じ配置先が複数回指定されています: (.+)$/, (m) => `The same destination is specified more than once: ${m[1]}`],
        [/^スキーマ検証 NG（(.+)）: (.+)$/, (m) => `Schema check failed (${m[1]}): ${m[2]}`],
        [/^LLM が有効な応答を返しませんでした: (.+)$/, (m) => `The LLM did not return a valid response: ${translateLine(m[1])}`],
        [/^OpenRouter が一時的なレート制限を返しました: (.*)$/, (m) => `OpenRouter returned a temporary rate limit: ${m[1]}`],
        [/^LLM 応答がタイムアウトしました（(\d+)秒）。サーバ側で生成は継続している可能性があります。時間を置いて再実行してください。$/, (m) => `The LLM response timed out (${m[1]}s). Generation may still be running server-side. Try again later.`],
        [/^OpenRouter へ接続できません: (.+)$/, (m) => `Cannot reach OpenRouter: ${m[1]}`],
        [/^OpenRouterからモデル単価を取得できません: (.+)$/, (m) => `Could not retrieve model pricing from OpenRouter: ${m[1]}`],
        [/^OpenRouterにモデルが存在しません: (.+)$/, (m) => `The model does not exist on OpenRouter: ${m[1]}`],
        [/^OpenRouterのモデル単価取得に失敗しました（HTTP (\d+)）(.*)$/, (m) => `Failed to retrieve OpenRouter model pricing (HTTP ${m[1]})${m[2]}`],
        [/^タスク (.+) のモデルが未設定です（config\/llm\.json）$/, (m) => `No model configured for task ${m[1]} (config/llm.json)`],
        [/^指定ユニットが見つかりません: (.+)（有効: (.+)）$/, (m) => `Unit not found: ${m[1]} (valid: ${m[2]})`],
        [/^サーバへ接続できません（(.+)）。ネットワーク接続を確認し、しばらくしてからページを再読み込みしてください$/, (m) => `Cannot reach the server (${m[1]}). Check your network connection and reload the page after a while.`],
        [/^GLBファイル変更: (.+)$/, (m) => `Replace GLB files: ${m[1]}`],
        [/^前回の手動変更: (.+) \/ (.+)$/, (m) => `Previous manual replacement: ${m[1]} / ${m[2]}`],
        [/^生成ログ: (.+)$/, (m) => `Generation logs: ${m[1]}`],
        [/^画像生成ログ（(\d+)件の履歴）$/, (m) => `Image generation log (${m[1]} ${m[1] === '1' ? 'run' : 'runs'})`],
        [/^画像プロンプト生成ログ（(\d+)件の履歴）$/, (m) => `Image-prompt generation log (${m[1]} ${m[1] === '1' ? 'run' : 'runs'})`],
        [/^GLB生成ログ（(\d+)件の履歴）$/, (m) => `GLB generation log (${m[1]} ${m[1] === '1' ? 'run' : 'runs'})`],
        [/^画像生成ログ（最終実行: (.+)）$/, (m) => `Image generation log (last run: ${m[1]})`],
        [/^GLB生成ログ（最終実行: (.+)）$/, (m) => `GLB generation log (last run: ${m[1]})`],
        [/^「(.+)」の (.+) を「(.+)」に変更しますか？$/, (m) => `Replace ${m[2]} for "${m[1]}" with "${m[3]}"?`],
        [/^✔ 変更完了: (.+)（(\d+)KB）$/, (m) => `✔ Replaced: ${m[1]} (${m[2]} KB)`],
        [/^（Meshy クレジット消費: (.+)〜 \/ 所要: 数分〜30分）$/, (m) => `(Meshy credits: ${m[1]}+ / Time: minutes to ~30 min)`],
        [/^  ── (.+)（(.+) \/ 人型敵: 3D→テクスチャ→リグ→アニメ×2）$/, (m) => `  ── ${m[1]} (${m[2]} / humanoid enemy: 3D → texture → rig → 2 animations)`],
        [/^    人型敵の戦闘アニメ選定: btl=(\d+)（(.+)）$/, (m) => `    Humanoid enemy battle animation: btl=${m[1]} (${m[2]})`],
        [/^    （人型敵の戦闘アニメ選定が失敗したため既定値を使用: (.+)）$/, (m) => `    (Humanoid enemy battle animation selection failed; using default: ${m[1]})`],
        [/^⚠ 見た目プロファイルのキャラ設定同期をスキップ: (.+)$/, (m) => `⚠ Skipped syncing the visual profile to character settings: ${m[1]}`],
        [/^✔ 画像プロンプト (\d+)件と見た目プロファイルを image-prompts\/ に保存しました（キャラ設定同期 (\d+)体）$/, (m) => `✔ Saved ${m[1]} image prompt(s) and visual profiles to image-prompts/ (synced ${m[2]} character setting(s))`],
    ];

    // ── ③ ラベル用トークン（部分置換。ラベル・接頭辞・単位のみ登録し、文章には使わない）──
    const TOKENS = {
        '物語: ': 'Story: ',
        'ゲーム: ': 'Game: ',
        'ゲームID: ': 'Game ID: ',
        'ワールド: ': 'Worlds: ',
        'キャラ: ': 'Characters: ',
        'AI実行: ': 'AI runs: ',
        'API キー: ': 'API key: ',
        '推定 ': 'Estimated ',
        'メールアドレス: ': 'Email: ',
        'ログイン方法: ': 'Sign-in method: ',
        '生成: ': 'Created: ',
        '進捗 ': 'Progress ',
        '次の作業: ': 'Next step: ',
        '開始地点: ': 'Start: ',
        ' 字）': ' chars)',
        '（入 ': '(in ',
        ' / 出 ': ' / out ',
        ' 回（': ' calls (',
        ' 回': ' calls',
        '： ': ': ',
        '（未確認）': '(unverified)',
        '設定済み（': 'Set (',
        '削除中…（完了までお待ちください）': 'Deleting… (please wait)',
        'プロジェクトを作成しています…': 'Creating project…',
        '画像をアップロードしています…': 'Uploading image…',
        '取り込みを実行しています…': 'Running import…',
        'キャラ設定を変更しています…': 'Applying character changes…',
        'ワールドを追加しています…': 'Adding world…',
        '版番を更新しています…': 'Bumping version…',
        'API キーを削除しています…': 'Deleting API key…',
        '現在の使用モデル: ': 'Current active model: ',
        'コスト重視: ': 'Lowest cost: ',
        '速度重視: ': 'Fastest: ',
        'アダルト緩和重視: ': 'Relaxed adult-content rules: ',
        '工程）': ' steps)',
        ' 体）': ')',
        ' 件': ' items',
        '種別': 'Type',
        '状態': 'Status',
        '対象': 'Target',
        '名前': 'Name',
        '内容': 'Details',
        'モデル: ': 'Model: ',
        '課金: ': 'Billing: ',
        '目安: ': 'Estimate: ',
        '実行しますか？': 'Proceed?',
        'アダルト表現の方針: ': 'Adult-content policy: ',
        '画像: ': 'Image: ',
        '手動差し替え': 'manual upload',
        '生成されたGLBのスキンウェイトが不正です: ': 'Generated GLB has invalid skin weights: ',
        '（編集可能）': '(editable)',
        '画像ファイル: ': 'Image file: ',
        '（任意）': '(optional)',
        '/ 整合:': ' / Integrity:',
        '/ 翻訳:': ' / i18n:',
        '/ アセット:': ' / Assets:',
        '整合:': 'Integrity:',
        '件中 不足 ': ' missing of ',
        ' 件。': ' items.',
        ' 件中': ' total',
        '/ 最終ログイン:': ' / Last sign-in:',
        '接続: ': 'Connection: ',
        '開始ワールド:': 'Starting world:',
        '作品タイトル:': 'Title:',
    };

    // ── 変換エンジン ─────────────────────────────
    const tokenKeys = Object.keys(TOKENS).sort((a, b) => b.length - a.length);
    const tokenRe = new RegExp(tokenKeys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'g');

    function translateLine(core) {
        const exact = EXACT[core];
        if (exact !== undefined) return exact;
        for (const [re, fn] of PATTERNS) {
            const m = core.match(re);
            if (m) return fn(m);
        }
        return core.replace(tokenRe, (k) => TOKENS[k]);
    }

    function translateText(input, forceLang = lang) {
        if (forceLang !== 'en' || typeof input !== 'string' || !CJK.test(input)) return input;
        const lead = input.match(/^\s*/)[0];
        const trail = input.match(/\s*$/)[0];
        const core = input.trim();
        if (core === '') return input;
        // テンプレートリテラル由来のテキストノードは「改行＋インデントを含む複数文」になるため、
        // 全体一致で外れたら行単位（辞書の収録単位）でも変換して結合し直す
        const whole = translateLine(core);
        if (whole !== core) return lead + whole + trail;
        if (core.includes('\n')) {
            const joined = core.split('\n').map((line) => {
                const l = line.match(/^\s*/)[0];
                const t = line.match(/\s*$/)[0];
                const c = line.trim();
                return c === '' ? line : l + translateLine(c) + t;
            }).join('\n');
            if (joined !== core) return lead + joined + trail;
        }
        return input;
    }

    /** 複数行文字列（alert/confirm 本文）を行単位で変換する。 */
    function translateBlock(text, forceLang = lang) {
        if (forceLang !== 'en' || typeof text !== 'string') return text;
        return String(text).split('\n').map((line) => translateText(line, forceLang)).join('\n');
    }

    // ── DOM 変換（テキストノード＋ title/placeholder/aria-label）────────
    const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE']);
    function shouldSkip(el, attribute = false) {
        for (let e = el; e; e = e.parentElement) {
            if (SKIP_TAGS.has(e.tagName)) return true;
            // textarea の入力値・ログ本文は翻訳しないが、placeholder / aria-label / title はUIなので翻訳する。
            if (!attribute && e.tagName === 'TEXTAREA') return true;
            if (e.classList?.contains('log')) return true; // ジョブログはサーバ出力＝原文のまま
            if (e.hasAttribute?.('data-no-i18n')) return true;
        }
        return false;
    }
    function translateDom(root) {
        if (!hasDom || lang !== 'en' || !root) return;
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            if (shouldSkip(node.parentElement)) continue;
            const out = translateText(node.data);
            if (out !== node.data) node.data = out;
        }
        const scope = root.nodeType === 1 ? [root, ...root.querySelectorAll('[title], [placeholder], [aria-label]')] : [];
        for (const el of scope) {
            if (shouldSkip(el, true)) continue;
            for (const attr of ['title', 'placeholder', 'aria-label']) {
                const v = el.getAttribute?.(attr);
                if (v) {
                    const out = translateBlock(v);
                    if (out !== v) el.setAttribute(attr, out);
                }
            }
        }
    }

    // ── 公開 API ─────────────────────────────
    const api = {
        lang,
        t: translateText,
        translateBlock,
        translateDom,
        setLang(next) {
            const target = next === 'en' ? 'en' : 'ja';
            if (typeof localStorage !== 'undefined') localStorage.setItem(STORE_KEY, target);
            if (!hasDom) return;
            // URL に /en /ja の明示指定がある場合は、その部分も切替先へ書き換えて遷移する
            //（そのまま reload すると URL 指定が localStorage より優先されて切り替わらないため）
            const m = /^\/(en|ja)(\/.*|$)/.exec(location.pathname);
            if (m) {
                location.href = `/${target}${m[2] || '/'}${location.search}${location.hash}`;
            } else {
                location.reload(); // 再描画で全画面へ一括反映（実行中は beforeunload ガードが確認する）
            }
        },
        _dict: { EXACT, PATTERNS, TOKENS }, // テスト用
    };
    globalThis.VN_I18N = api;

    if (!hasDom) return; // Node（テスト）から import された場合はここまで

    // alert/confirm/prompt を変換つきでラップ（画面と同じ辞書でダイアログも翻訳）
    const rawAlert = window.alert.bind(window);
    const rawConfirm = window.confirm.bind(window);
    window.alert = (msg) => rawAlert(translateBlock(String(msg ?? '')));
    window.confirm = (msg) => rawConfirm(translateBlock(String(msg ?? '')));

    const start = () => {
        document.documentElement.lang = lang;
        if (lang === 'en') document.title = translateText(document.title);
        // サイドバー内の言語切替と公式サイトへのリンクも、現在の表示言語へ揃える。
        const sidebarLang = document.getElementById('sidebar-lang');
        if (sidebarLang) {
            sidebarLang.value = lang;
            sidebarLang.addEventListener('change', () => api.setLang(sidebarLang.value));
        }
        for (const homeLink of document.querySelectorAll('[data-vn-home]')) {
            homeLink.href = `https://voxelnovels.com/${lang}/`;
        }
        for (const pageLink of document.querySelectorAll('[data-vn-page]')) {
            pageLink.href = `https://voxelnovels.com/${lang}/${pageLink.dataset.vnPage}/`;
        }
        // 言語切替（リストボックス。ヘッダの他ボタンと同じ「アイコン＋下段コントロール」の 2 行構成）
        const nav = document.querySelector('header nav');
        if (nav && !document.getElementById('nav-lang')) {
            const wrap = document.createElement('div');
            wrap.className = 'nav-lang';
            wrap.innerHTML = '<span class="nav-ico" aria-hidden="true">🌐</span>'
                + '<select id="nav-lang" aria-label="Language"><option value="ja">日本語</option><option value="en">English</option></select>';
            const sel = wrap.querySelector('select');
            sel.value = lang;
            sel.title = lang === 'en' ? 'Display language' : '表示言語';
            sel.addEventListener('change', () => api.setLang(sel.value));
            nav.appendChild(wrap);
        }
        translateDom(document.body);
        if (lang !== 'en') return; // 日本語表示なら監視不要
        const observer = new MutationObserver((records) => {
            for (const r of records) {
                if (r.type === 'characterData') {
                    if (!shouldSkip(r.target.parentElement)) {
                        const out = translateText(r.target.data);
                        if (out !== r.target.data) r.target.data = out;
                    }
                } else if (r.type === 'childList') {
                    for (const node of r.addedNodes) {
                        if (node.nodeType === 3) {
                            if (!shouldSkip(node.parentElement)) {
                                const out = translateText(node.data);
                                if (out !== node.data) node.data = out;
                            }
                        } else if (node.nodeType === 1) {
                            translateDom(node);
                        }
                    }
                } else if (r.type === 'attributes' && r.target.nodeType === 1 && !shouldSkip(r.target, true)) {
                    const v = r.target.getAttribute(r.attributeName);
                    if (v) {
                        const out = translateBlock(v);
                        if (out !== v) r.target.setAttribute(r.attributeName, out);
                    }
                }
            }
        });
        observer.observe(document.body, {
            subtree: true, childList: true, characterData: true,
            attributes: true, attributeFilter: ['title', 'placeholder', 'aria-label'],
        });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();
